import { supabase } from "@/lib/supabase/client";
import {
  loadAuthTokens,
  saveAuthTokens,
  clearAuthTokens,
  getAuthBearerToken,
} from "@/lib/authToken";
import { getDeviceId } from "@/lib/deviceId";
import {
  cacheApiResponse,
  getCachedApiResponse,
  invalidateApiResponse,
  isCacheableJson,
} from "@/lib/offlinePersistence";

/** Normalize an unknown value into a plain record (no-op for objects). */
export function recordOf(value: unknown): any {
  return typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)
    : {};
}

export interface Thread {
  id: string;
  title: string;
  body: string;
  images?: string[];
  author: {
    id?: string;
    name: string;
    username?: string;
    avatar?: string;
    role?: string;
    initials: string;
  };
  createdAt: string;
  tag: string;
  course_code?: string;
  is_pinned?: boolean;
  pinned?: boolean;
  views?: number;
  author_name?: string;
  author_username?: string;
  author_id?: string;
}

export interface Community {
  id: string;
  name: string;
  course_code: string;
  level: number;
  description: string;
  members: number;
  member_count?: number;
  is_member: boolean;
  is_permanent?: boolean;
}

export interface StudyGroup {
  id: string;
  name: string;
  course?: string;
  course_code?: string;
  description?: string;
  members: number;
  member_count?: number;
  is_member?: boolean;
  is_permanent?: boolean;
  is_locked?: boolean;
  is_private?: boolean;
  invite_token?: string;
  join_code?: string;
  created_by?: string;
  created_by_username?: string;
  creator_username?: string;
  creator_name?: string;
  created_at?: string;
}

export interface UserQuota {
  free_remaining: number;
  extra_credits: number;
  daily_limit?: number;
  used_today?: number;
  remaining?: number;
  is_premium?: boolean;
  plan?: string;
  study_groups_owned?: number;
  study_groups_limit?: number;
  study_groups_locked_count?: number;
  next_reset_timestamp?: string;
  suspended?: boolean;
  notes_limit?: number;
  exports_remaining?: number;
  exports_limit?: number;
}

export interface GlossaryTerm {
  id: string;
  term: string;
  definition: string;
}

export interface Note {
  id: string;
  title: string;
  preview: string;
  type: string;
  time: string;
  pinned: boolean;
  course: string;
}

export interface Announcement {
  id: string;
  announcement_id: string;
  course_code: string;
  course_name: string;
  author_name: string;
  author_role: string;
  author?: string;
  title: string;
  content: string;
  pinned: boolean;
  created_at: string;
  // Backend may also return snake_case variants
  __authorName?: string;
  createdAt?: string;
}

export interface Ticket {
  id: string;
  status: string;
  priority: string;
  subject: string;
  user: string;
  user_email?: string;
  created_at: string;
  messages?: unknown[];
}

export interface Course {
  id: string;
  name: string;
  code: string;
  department?: string;
}

export interface CourseRepStats {
  total_students?: number;
  questions_week?: number;
  avg_confidence?: number;
  materials_uploaded?: number;
  study_groups?: number;
  chunk_coverage?: Array<{
    week?: number;
    week_number?: number;
    chunks?: number;
    count?: number;
  }>;
}

export interface CourseRepAnalytics {
  top_questions?: TopQuestion[];
  weak_topics?: WeakTopic[];
  chunk_coverage?: Array<{
    week?: number;
    week_number?: number;
    chunks?: number;
    count?: number;
  }>;
  engagement?: number;
  engagement_trend?: unknown[];
  readiness_distribution?: unknown[];
}

export interface TopQuestion {
  id?: string;
  question?: string;
  title?: string;
  topic?: string;
  name?: string;
  count?: number;
  total?: number;
  // Backend returns confidence under multiple key variants; use effectiveConfidence() to_get_the_value
  __avgConfidence?: number;
  avg_confidence?: number;
  average_confidence?: number;
  confidence?: number;
}

export interface WeakTopic {
  id?: string;
  topic?: string;
  name?: string;
  title?: string;
  // Backend returns score under multiple key variants
  avg_confidence?: number;
  average_confidence?: number;
  confidence?: number;
  count?: number;
  score?: number;
}

export interface CourseRepStudent {
  id: string;
  name?: string;
  student_name?: string;
  studentId?: string;
  // Normalized display name
  displayName?: string;
  // Backend returns under multiple key variants; normalize_to_studentId___studentId?: string;
  email?: string;
  avatar?: string;
  // Backend returns avatar_url; normalize_to_avatar_avatar_url?: string | null;
  questionsAsked?: number;
  // Backend returns questionsAsked; normalize_to_questions_asked_questionsAsked?: number;
  // Backend returns confidence under multiple key variants
  avg_confidence?: number;
  average_confidence?: number;
  avgConfidence?: number;
  averageConfidence?: number;
  // Backend returns weakest topic under multiple key variants
  weakest_topic?: string;
  weakestTopic?: string;
  level?: string;
  department?: string;
}

export interface CourseRepAnnouncement {
  id: string;
  title: string;
  body?: string;
  content?: string;
  author?: string;
  author_name?: string;
  author_role?: string;
  date?: string;
  created_at?: string;
  pinned?: boolean;
  course?: string;
  course_id?: string;
  course_code?: string;
}

export interface Reply {
  id: string;
  threadId: string;
  body: string;
  images?: string[];
  author: {
    id?: string;
    name: string;
    username?: string;
    avatar?: string;
    role?: string;
    initials: string;
  };
  createdAt: string;
}

export interface Review {
  id: string;
  student_name: string;
  rating: number;
  review_text: string;
  created_at: string;
  helpful_count: number;
}

export interface AchievementBadge {
  id: string;
  name: string;
  description: string;
  icon: string;
  dateEarned?: string;
  isLocked: boolean;
}

export interface Skill {
  id: string;
  name: string;
  description: string;
  unlocked: boolean;
  unlockedBy: string;
}

export interface Challenge {
  id: string;
  title: string;
  description: string;
  target: number;
  current: number;
  reward: number;
  expiresAt: string;
  leaderboard: { name: string; score: number; avatar?: string }[];
}

export interface Formula {
  id: string;
  topic: string;
  latex: string;
  description: string;
}

export interface FormulaEntry {
  id: string;
  topic: string;
  parent_topic?: string;
  formula_latex: string;
  definition: string;
  page_number?: number;
  section_hierarchy?: string;
  source?: string;
}

export interface Flashcard {
  id: string;
  question: string;
  answer: string;
  front?: string;
  back?: string;
  topic?: string;
  difficulty?: number;
  next_review?: string | null;
  source?: string;
}

export interface PastQuestion {
  id: string;
  year: string;
  type?: "midterm" | "final";
  url?: string;
  course_code?: string;
  question_text?: string;
  options?: string[];
  correct_answer?: string;
  explanation?: string;
  topic?: string;
  difficulty?: string;
}

export interface Resource {
  id: string;
  title: string;
  description: string;
  url: string;
  original_filename: string;
  material_type:
    | "notes"
    | "slides"
    | "past_question"
    | "audio"
    | "video"
    | string;
  created_at: string;
  suggestedBy?: string;
}

export interface Tutor {
  id: string;
  name: string;
  bio: string;
  subjects: string[];
  rating: number;
  rate?: number;
  status: "pending" | "active" | "suspended";
  avatar?: string;
}

export interface TimeSlot {
  id: string;
  start: string;
  end: string;
  available: boolean;
}

export interface GroupMessage {
  id: string;
  group_id: string;
  sender_id: string;
  sender_name: string;
  body: string;
  /** E2E fields — present when the message was encrypted client-side */
  body_encrypted?: string;
  e2e_iv?: string;
  e2e_key_id?: string;
  created_at: string;
}

export interface AdminStats {
  totalUsers: number;
  activeUsers: number;
  questionsAskedToday: number;
  revenue: number;
}

export interface AdminPayment {
  id: string;
  txRef: string;
  amount: number;
  user: string;
  date: string;
  status: "pending" | "verified" | "rejected" | string;
  narration?: string;
}

export interface Invoice {
  id: string;
  date: string;
  amount: number;
  status: "paid" | "pending" | "failed";
  downloadUrl: string;
}

export interface PaymentMethod {
  id: string;
  type: "card" | "bank";
  last4?: string;
  bankName?: string;
  isDefault: boolean;
}

export interface QuizQuestion {
  id: string;
  text: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

export interface Analytics {
  timePerCourse: { course: string; minutes: number }[];
  accuracy: number;
  questionsAsked: number;
  streak: number;
}

export interface ComparisonData {
  percentile: number;
  avgQuestions: number;
  userQuestions: number;
}

export interface UserSettings {
  notifications: {
    email: boolean;
    inApp: boolean;
    replies: boolean;
    payments: boolean;
    approvals: boolean;
    exam_reminders: boolean;
    course_announcements: boolean;
    examCampaign?: boolean;
  };
  twoFactorEnabled: boolean;
  language: string;
  translate_answers?: boolean;
  accessibility: {
    highContrast: boolean;
    reducedMotion: boolean;
    fontSize: "small" | "medium" | "large";
  };
}

export interface User {
  id: string;
  name: string;
  full_name?: string;
  email: string;
  role: "student" | "admin" | "course_rep" | "tutor";
  status: "active" | "suspended";
  avatar?: string;
  avatar_url?: string | null;
  username?: string;
  institution?: string;
  date?: string;
  is_premium?: boolean;
}

export interface PublicUser {
  id?: string;
  user_id?: string;
  name?: string;
  full_name?: string;
  avatar?: string;
  avatar_url?: string | null;
  role?: string;
  level?: number | string | null;
  school_code?: string;
  department_code?: string;
  institution?: string;
  is_verified?: boolean;
  kyc_status?: string;
  video_url?: string | null;
  badges?: AchievementBadge[];
  contributions?: {
    threads: number;
    replies: number;
    credits: number;
    total: number;
  };
  status_videos?: Array<{
    id: string;
    video_url: string;
    caption?: string;
    created_at?: string;
  }>;
  stats?: {
    questions: number;
    answers: number;
    followers: number;
    following: number;
  };
}

export interface UserProfile extends User {
  settings: UserSettings;
  referralCode: string;
  referralStats: {
    joined: number;
    earned: number;
  };
  points: number;
  bio?: string;
  preferredName?: string;
  updated_at?: string;
  phone?: string;
  school?: string;
  department?: string;
  level?: string;
  semester?: string;
  free_remaining?: number;
  study_streak?: number;
  longest_streak?: number;
  application_status?: "pending" | "approved" | "rejected";
  major?: string;
  year?: string;
  application_date?: string;
  created_at?: string;
  rejection_notes?: string;
  reviewer_notes?: string;
  extra_credits?: number;
}

export interface UserSkill {
  id: string;
  name: string;
  description: string;
  unlocked: boolean;
  unlocked_by: string;
}

export interface UserIdentity {
  user_id: string;
  role: string;
  level: number | null;
  is_course_rep: boolean;
  is_verified: boolean;
  badges: AchievementBadge[];
  skills: UserSkill[];
  contributions: {
    threads: number;
    replies: number;
    credits: number;
    total: number;
  };
}

export interface AppEvent {
  id: string;
  title: string;
  description: string;
  date: string;
  time?: string;
  start_time?: string;
  location?: string;
  category?: string;
  course?: string;
  type?: "exam" | "study" | "deadline" | "tutorial";
  isRSVPed?: boolean;
}

export interface Session {
  id: string;
  tutorId: string;
  tutorName: string;
  startTime: string;
  endTime: string;
  status: "upcoming" | "completed" | "cancelled";
  joinUrl?: string;
}

export interface SecurityEvent {
  id: string;
  event: string;
  status: "success" | "failed" | "warning";
  timestamp: string;
  ip: string;
  location: string;
}

export interface SystemStatus {
  services: {
    name: string;
    status: "online" | "degraded" | "offline";
    latency: number;
  }[];
}

// Helper for authenticated requests
const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_URL || "https://api.uniui.com.ng";
export { API_BASE_URL };
const API_VERSION_PREFIX = "/api/v1";

// Legacy/frontend path → actual backend path.
// Fixes sync issues where the frontend calls an old path that the backend
// has reorganized. Checked AFTER the /api/v1 prefix is applied.
const PATH_REMAPS: Record<string, string> = {
  "/api/v1/admin/health": "/api/v1/health",
  "/api/v1/admin/config": "/api/v1/config",
  "/api/v1/admin/uptime": "/api/v1/admin/backup-status",
  "/api/v1/admin/backups": "/api/v1/admin/backup-status",
  "/api/v1/admin/promo-codes": "/api/v1/promo/admin/codes",
  "/api/v1/admin/promo-codes/stats": "/api/v1/promo/admin/codes/stats",
  "/api/v1/eval/summary": "/api/v1/eval/summary",
  "/api/v1/eval/history": "/api/v1/eval/history",
  "/api/v1/events/rsvp": "/api/v1/user/events/rsvp",
  "/api/v1/notifications/mark-all-read": "/api/v1/notifications/read-all",
  "/api/v1/past-questions/stats": "/api/v1/past-question-stats",
};

// Prefix-based remaps (for dynamic segments / path params)
const PATH_PREFIX_REMAPS: Array<[string, string]> = [
  // /api/v1/admin/promo-codes/{id} → /api/v1/promo/admin/codes/{id}
  ["/api/v1/admin/promo-codes/", "/api/v1/promo/admin/codes/"],
];

const remapPath = (p: string): string => {
  if (PATH_REMAPS[p]) return PATH_REMAPS[p];
  for (const [from, to] of PATH_PREFIX_REMAPS) {
    if (p.startsWith(from)) return to + p.slice(from.length);
  }
  return p;
};

// Helper to ensure endpoints always use the versioned prefix
export const versioned = (endpoint: string): string => {
  const stripped = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
  // If_already_versioned, apply remaps then return
  let normalized = stripped.startsWith(API_VERSION_PREFIX) ? stripped : "";
  if (!normalized) {
    // Legacy root paths
    if (stripped === "/ask") normalized = `${API_VERSION_PREFIX}/ask`;
    else if (stripped === "/leaderboard")
      normalized = `${API_VERSION_PREFIX}/leaderboard`;
    else if (stripped === "/user/quota")
      normalized = `${API_VERSION_PREFIX}/user/quota`;
    else if (stripped === "/notifications/read")
      normalized = `${API_VERSION_PREFIX}/notifications/mark-read`;
    else if (stripped.startsWith("/tutors/"))
      normalized = `${API_VERSION_PREFIX}/tutors${stripped.slice("/tutors".length)}`;
    else if (stripped === "/course-rep/upload")
      normalized = `${API_VERSION_PREFIX}/content/upload-material`;
    else if (stripped === "/auth/reset-password")
      normalized = `${API_VERSION_PREFIX}/auth/password-reset`;
    else if (stripped === "/daily-bonus/claim")
      normalized = `${API_VERSION_PREFIX}/daily-bonus`;
    else if (stripped === "/courses/")
      normalized = `${API_VERSION_PREFIX}/courses`;
    else if (stripped.startsWith("/courses/") && !stripped.includes("/api/"))
      normalized = `${API_VERSION_PREFIX}${stripped}`;
    else if (stripped.startsWith("/task/"))
      normalized = `${API_VERSION_PREFIX}/jobs${stripped.slice("/task".length)}`;
    else normalized = `${API_VERSION_PREFIX}${stripped}`;
  }
  return remapPath(normalized);
};

// Helper to build consistent backend request headers.
// The_backend_is_hosted_on_a_VPS, so every request carries identifying headers
// for_device_tracking, request_tracing, and locale. Auth is handled separately
// via the custom-auth JWT (getAuthBearerToken) or Supabase session token.
export function getBackendHeaders(token?: string): Record<string, string> {
  const deviceId = typeof window !== "undefined" ? getDeviceId() : "";
  const headers: Record<string, string> = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(token ? { "X-Supabase-Token": token } : {}),
    ...(deviceId ? { "X-Device-Id": deviceId } : {}),
    "X-Request-ID": crypto.randomUUID(),
    "Accept-Language":
      typeof navigator !== "undefined"
        ? navigator.language || "en-US"
        : "en-US",
  };
  return headers;
}

export async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  // EMERGENCY: retries disabled while the backend is returning 503/unavailable.
  // Retries amplify load on an already-down service and flood the console with
  // `[apiFetch] ... failed, retrying in ...` messages. Restore after recovery.
  // TODO: set back to 1 once backend/CORS are healthy.
  const MAX_RETRIES = 0;
  const RETRY_DELAYS: number[] = [];
  const isRetryableMethod =
    !options.method || options.method === "GET" || options.method === "HEAD";

  const attempt = async function attempt(attemptIndex: number): Promise<T> {
    const isGet = !options.method || options.method === "GET";
    const versionedEndpoint = versioned(endpoint);
    const fullUrl = `${API_BASE_URL}${versionedEndpoint}`;
    const shouldCache = isGet && !versionedEndpoint.includes("/auth/");
    // Only invalidate the cache for mutating requests (POST/PUT/PATCH/DELETE).
    // Invalidation-before-GET was wiping the read-through cache on every call,
    // which made the cache useless for the dashboard's many GET requests and
    // forced a network round-trip even when a fresh response was already warm.
    const isMutation =
      options.method && !["GET", "HEAD"].includes(options.method.toUpperCase());
    if (
      isMutation &&
      !versionedEndpoint.includes("/auth/") &&
      versionedEndpoint.startsWith("/api/v1/")
    ) {
      await invalidateApiResponse(fullUrl).catch(() => {});
    }
    try {
      const customToken = getAuthBearerToken();
      let token = customToken;
      if (!token && supabase) {
        try {
          const {
            data: { session },
          } = await supabase.auth.getSession();
          token = session?.access_token || "";
        } catch {
          token = "";
        }
      }

      const isFormData = options.body instanceof FormData;
      const headers: HeadersInit = {
        ...(isFormData ? {} : { "Content-Type": "application/json" }),
        ...getBackendHeaders(token),
        ...options.headers,
      };

      const response = await fetch(fullUrl, {
        ...options,
        headers,
      });

      if (response.status === 401 && attemptIndex === 0) {
        const refreshed = await tryRefreshCustomAuth();
        if (refreshed) {
          const newToken = getAuthBearerToken();
          const retryHeaders: HeadersInit = {
            ...(isFormData ? {} : { "Content-Type": "application/json" }),
            ...getBackendHeaders(newToken),
            ...options.headers,
          };
          const retryResponse = await fetch(fullUrl, {
            ...options,
            headers: retryHeaders,
          });
          const refreshData = await resolveResponse<T>(retryResponse, endpoint);
          if (shouldCache && isCacheableJson(retryResponse)) {
            cacheApiResponse(fullUrl, refreshData).catch(() => {});
          }
          return refreshData;
        }
      }

      const data = await resolveResponse<T>(response, endpoint);
      if (shouldCache && isCacheableJson(response)) {
        cacheApiResponse(fullUrl, data).catch(() => {});
      }
      return data;
    } catch (err) {
      const isNetworkError = err instanceof TypeError;
      if (isGet && isNetworkError && attemptIndex === 0) {
        const cached = await getCachedApiResponse<T>(fullUrl).catch(() => null);
        if (cached !== null) {
          if (typeof window !== "undefined") {
            console.info(
              `[apiFetch] offline — serving cached response for ${endpoint}`,
            );
          }
          return cached;
        }
      }
      const status = (err as any)?.status;
      const isRetryable =
        isRetryableMethod &&
        (err instanceof TypeError ||
          status === 503 ||
          status === 504 ||
          (typeof status === "number" && status >= 500));
      if (attemptIndex < MAX_RETRIES && isRetryable) {
        const delay = RETRY_DELAYS[attemptIndex] ?? 2000;
        if (typeof window !== "undefined") {
          console.warn(
            `[apiFetch] ${endpoint} failed, retrying in ${delay}ms...`,
            err,
          );
        }
        await new Promise((resolve) => setTimeout(resolve, delay));
        return attempt(attemptIndex + 1);
      }
      throw err;
    }
  };

  return attempt(0);
}

// Resolve a fetch Response into typed data or throw a structured error.
// Centralized so both the primary request and the post-refresh retry share it.
async function resolveResponse<T>(
  response: Response,
  endpoint: string,
): Promise<T> {
  let jsonData: unknown;
  try {
    jsonData = await response.json();
  } catch {
    if (
      response.status === 503 ||
      (response.ok && typeof jsonData === "string")
    ) {
      const error = new Error("Service unavailable") as Error & {
        status?: number;
        data?: unknown;
      };
      error.status = response.status;
      throw error;
    }
    jsonData = {};
  }

  if (
    jsonData &&
    typeof jsonData === "object" &&
    (jsonData as any).status === "error"
  ) {
    const errorData = jsonData as any;
    const error = new Error(
      errorData.message ||
        errorData.detail ||
        `API error: ${response.statusText}`,
    ) as Error & { status?: number; data?: unknown; retryable?: boolean };
    error.status = errorData.code || response.status;
    error.data = errorData;
    error.retryable = (error.status || 0) >= 500;
    throw error;
  }

  if (!response.ok) {
    const errorData = typeof jsonData === "object" ? jsonData : {};
    const effectiveStatus =
      response.status === 403 && !(errorData as any)?.authenticated
        ? 401
        : response.status;
    const error = new Error(
      typeof (errorData as any)?.detail === "string"
        ? (errorData as any).detail
        : (errorData as any)?.message || `API error: ${response.statusText}`,
    ) as Error & { status?: number; data?: unknown; retryable?: boolean };
    error.status = effectiveStatus;
    error.data = errorData;
    error.retryable =
      effectiveStatus === 503 ||
      effectiveStatus === 504 ||
      effectiveStatus >= 500;
    throw error;
  }

  return jsonData as T;
}

// Attempt to refresh the backend custom-auth session using the stored refresh
// token. Returns true if a fresh access token was obtained and persisted.
// Never throws — a Supabase outage must not crash or log the user out.
async function tryRefreshCustomAuth(): Promise<boolean> {
  const tokens = loadAuthTokens();
  if (!tokens?.refresh_token) return false;
  try {
    const data = await apiFetchRaw<{
      access_token: string;
      refresh_token: string;
      user?: Record<string, unknown>;
    }>("/api/v1/auth/refresh", {
      method: "POST",
      body: JSON.stringify({ refresh_token: tokens.refresh_token }),
    });
    if (data?.access_token) {
      saveAuthTokens({
        access_token: data.access_token,
        refresh_token: data.refresh_token || tokens.refresh_token,
        user: data.user || tokens.user,
      });
      return true;
    }
  } catch {
    // Refresh failed (expired/invalid) — fall through to logged-out state.
    clearAuthTokens();
  }
  return false;
}

// Low-level fetch that does NOT auto-refresh (used by tryRefreshCustomAuth to
// avoid infinite recursion). Carries the current custom-auth bearer token.
async function apiFetchRaw<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const baseUrl = API_BASE_URL;
  const versionedEndpoint = versioned(endpoint);
  const token = getAuthBearerToken();
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...getBackendHeaders(token),
    ...options.headers,
  };
  const response = await fetch(`${baseUrl}${versionedEndpoint}`, {
    ...options,
    headers,
  });
  return resolveResponse<T>(response, endpoint);
}

// ============================================================================
// Telegram Login Widget
// ============================================================================

// Payload returned by the official Telegram Login Widget's onauth callback.
export interface TelegramAuthData {
  id: string | number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: string | number;
  hash: string;
}

export interface TelegramLoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  user: {
    id: string;
    user_id?: string;
    email: string;
    name?: string;
    full_name?: string;
    telegram_id?: string;
    telegram_verified?: boolean;
    avatar_url?: string | null;
    face_photo_url?: string | null;
    onboarding_completed?: boolean;
    profile_complete?: boolean;
    plan?: string;
    credits?: number;
  };
  is_new_user: boolean;
  requires_onboarding?: boolean;
}

// Exchange a Telegram Login Widget auth payload for a Supabase session.
// The backend verifies the hash server-side and issues the tokens.
export async function telegramLogin(
  data: TelegramAuthData,
): Promise<TelegramLoginResponse> {
  return apiFetch<TelegramLoginResponse>(versioned("/api/v1/auth/telegram"), {
    method: "POST",
    body: JSON.stringify(data),
  });
}

const api = {
  // Common
  getCourses: async (): Promise<Course[]> => {
    try {
      // Prefer the lightweight public course-codes endpoint.
      const codes = await apiFetch<string[]>(versioned("/api/v1/courses"));
      if (Array.isArray(codes) && codes.length > 0) {
        return codes.map((code) => ({
          id: code,
          name: code,
          code,
          department: "Academic",
        }));
      }
      // Fallback: enrich from materials if the codes endpoint is empty.
      const materials = await apiFetch<unknown[]>(
        versioned("/api/v1/materials"),
      );
      const byCode = new Map<string, any>();
      for (const m of materials as any[]) {
        const code = m.course_code;
        if (code && !byCode.has(code)) byCode.set(code, m);
      }
      return Array.from(byCode.entries()).map(([code, m]) => ({
        id: m.id || code,
        name: m.course_name || m.title || code,
        code,
        department: m.department || m.school || "Academic",
      }));
    } catch (error) {
      console.warn("Failed to fetch courses from backend", error);
      return [];
    }
  },

  getUserProfile: async (): Promise<UserProfile> => {
    return apiFetch(versioned("/api/v1/user/profile"));
  },
  updateUserSettings: async (
    settings: Partial<UserSettings>,
  ): Promise<void> => {
    return apiFetch(versioned("/api/v1/user/settings"), {
      method: "PATCH",
      body: JSON.stringify(settings),
    });
  },
  updateProfile: async (data: {
    full_name?: string;
    institution?: string;
    school_code?: string;
    department_code?: string;
    level?: number | string;
    semester?: string;
    bio?: string;
    major?: string;
    grad_year?: number | string;
    gpa?: number | string;
    location?: string;
    pronouns?: string;
    interests?: string[] | string;
    social_links?: Record<string, string>;
    courses?: string[] | string;
  }): Promise<any> => {
    return apiFetch(versioned("/api/v1/user/profile"), {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  },

  /**
   * manageCourses — add or remove a single enrolled course code.
   * Reads the current courses array from the profile, mutates it, and
   * PATCHes the whole array back. Backed by the same /user/profile endpoint
   * as updateProfile; no separate /courses endpoint exists.
   */
  manageCourses: async (
    action: "add" | "remove",
    courseCode: string,
  ): Promise<string[]> => {
    const profile = await apiFetch<Record<string, unknown>>(
      versioned("/api/v1/user/profile"),
    );
    const current: string[] = Array.isArray((profile as any)?.courses)
      ? (profile as any).courses
      : [];
    const MAX_ENROLLED = 20;
    const updated =
      action === "add"
        ? (() => {
            const next = Array.from(
              new Set([...current, courseCode.trim().toUpperCase()]),
            );
            // Enforce the 20-course cap by swapping out the oldest enrolment
            // when a new course is added at the limit (#13).
            while (next.length > MAX_ENROLLED) next.shift();
            return next;
          })()
        : current.filter((c) => c !== courseCode);
    await apiFetch(versioned("/api/v1/user/profile"), {
      method: "PATCH",
      body: JSON.stringify({ courses: updated }),
    });
    return updated;
  },

  /**
   * uploadAvatar — POST a profile picture to Cloudinary via the backend
   * proxy (avoids leaking Cloudinary credentials to the browser).
   * Returns the resolved avatar URL.
   */
  uploadAvatar: async (
    file: File,
    provider: "cloudinary" | "supabase" = "cloudinary",
  ): Promise<{ avatar_url: string; avatar?: string }> => {
    const form = new FormData();
    form.append("file", file);
    form.append("provider", provider);
    const res = await apiFetch<any>(versioned("/api/v1/user/avatar/upload"), {
      method: "POST",
      body: form,
    });
    return {
      avatar_url: res?.avatar_url || res?.avatar || "",
      avatar: res?.avatar_url || res?.avatar || "",
    };
  },

  // Tokens (non-monetary in-app currency)
  getTokenBalance: async (): Promise<{
    tokens: number;
    stepwise_credits: number;
    extra_credits: number;
    exchange_rates: { question_credit: number; stepwise: number };
  }> => {
    try {
      return await apiFetch(versioned("/api/v1/tokens/balance"));
    } catch {
      return {
        tokens: 0,
        stepwise_credits: 0,
        extra_credits: 0,
        exchange_rates: { question_credit: 100, stepwise: 200 },
      };
    }
  },
  getTokenUsage: async (): Promise<{
    is_guest: boolean;
    questions_today: number;
    questions_limit: number;
    questions_remaining: number;
    notes_today: number;
    notes_limit: number;
    exports_this_month: number;
    exports_limit: number;
    exports_remaining: number;
    tokens: number;
    extra_credits: number;
    plan: string;
  }> => {
    try {
      // Prefer the canonical quota endpoint that reads the same debit counter the
      // ask flow writes (profiles.questions_used_today). /api/v1/tokens/usage is
      // not_served_by_the_current_backend, so we fall back below on any failure.
      const d: any = await apiFetch(versioned("/api/v1/user/quota"));
      return {
        is_guest: false,
        questions_today: d?.used_today ?? 0,
        questions_limit: d?.daily_limit ?? 5,
        questions_remaining: d?.remaining ?? d?.free_remaining ?? 0,
        notes_today: 0,
        notes_limit: 100000, // onboarded users get unlimited notes across all courses
        exports_this_month: 0,
        exports_limit: 30,
        exports_remaining: 30,
        tokens: 0,
        extra_credits: d?.extra_credits ?? 0,
        plan: d?.plan ?? "free",
      };
    } catch {
      return {
        is_guest: true,
        questions_today: 0,
        questions_limit: 1,
        questions_remaining: 1,
        notes_today: 0,
        notes_limit: 20,
        exports_this_month: 0,
        exports_limit: 0,
        exports_remaining: 0,
        tokens: 0,
        extra_credits: 0,
        plan: "guest",
      };
    }
  },
  claimDailyTokenBonus: async (): Promise<{
    granted: number;
    already_claimed_today?: boolean;
    tokens: number;
  }> => {
    try {
      return await apiFetch(versioned("/api/v1/tokens/daily-bonus"), {
        method: "POST",
      });
    } catch {
      return { granted: 0, tokens: 0 };
    }
  },
  earnTokens: async (
    source: "referral" | "promo" | "friend" | "refund",
    opts?: { amount?: number; code?: string; from_user_id?: string },
  ) => {
    return apiFetch(versioned("/api/v1/tokens/earn"), {
      method: "POST",
      body: JSON.stringify({ source, ...opts }),
    });
  },
  convertTokens: async (
    target: "question_credit" | "stepwise",
    amount: number,
  ) => {
    return apiFetch(versioned("/api/v1/tokens/convert"), {
      method: "POST",
      body: JSON.stringify({ target, amount }),
    });
  },
  transferTokens: async (opts: {
    to_username?: string;
    to_user_id?: string;
    amount: number;
  }) => {
    return apiFetch(versioned("/api/v1/tokens/transfer"), {
      method: "POST",
      body: JSON.stringify(opts),
    });
  },

  // Events
  getEvents: async (): Promise<AppEvent[]> => {
    return apiFetch(versioned("/api/v1/events"));
  },
  createEvent: async (data: {
    title: string;
    date: string;
    type: string;
    course?: string;
    location?: string;
    endTime?: string;
  }): Promise<AppEvent> => {
    return apiFetch(versioned("/api/v1/events"), {
      method: "POST",
      body: JSON.stringify(data),
    });
  },
  updateEvent: async (
    id: string,
    data: Partial<AppEvent>,
  ): Promise<AppEvent> => {
    return apiFetch(versioned(`/api/v1/events/${id}`), {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },
  deleteEvent: async (id: string): Promise<void> => {
    return apiFetch(versioned(`/api/v1/events/${id}`), { method: "DELETE" });
  },
  getEvent: async (eventId: string): Promise<AppEvent> => {
    return apiFetch(versioned(`/api/v1/events/${eventId}`));
  },
  rsvpEvent: async (eventId: string): Promise<void> => {
    return apiFetch(versioned(`/api/v1/user/events/${eventId}/rsvp`), {
      method: "POST",
    });
  },

  // Analytics
  getCourseFeedback: async (
    courseId: string,
    limit: number = 20,
    offset: number = 0,
  ): Promise<Review[]> => {
    return apiFetch<unknown[]>(
      versioned(
        `/api/v1/courses/${encodeURIComponent(courseId)}/feedback?limit=${limit}&offset=${offset}`,
      ),
    ) as Promise<Review[]>;
  },

  // Course Rep
  getCourseRepStats: async (course?: string): Promise<CourseRepStats> => {
    return apiFetch<CourseRepStats>(
      versioned(
        `/api/v1/course-rep/stats?course=${encodeURIComponent(course || "")}`,
      ),
    );
  },
  getCourseRepAnalytics: async (
    course: string,
  ): Promise<CourseRepAnalytics> => {
    const data = await apiFetch<any>(
      versioned(
        `/api/v1/course-rep/analytics?course=${encodeURIComponent(course)}`,
      ),
    );
    return {
      top_questions: Array.isArray(data.top_topics)
        ? data.top_topics.map((t: any) => ({
            question: t.topic || "Untitled",
            count: t.count || 0,
            avgConfidence: t.avg_confidence || 0,
          }))
        : [],
      weak_topics: Array.isArray(data.weak_topics) ? data.weak_topics : [],
      engagement: data.engagement_index || 0,
      engagement_trend: Array.isArray(data.engagement_trend)
        ? data.engagement_trend
        : [],
      chunk_coverage: Array.isArray(data.chunk_coverage)
        ? data.chunk_coverage
        : [],
      readiness_distribution: Array.isArray(data.readiness_distribution)
        ? data.readiness_distribution
        : [],
    };
  },
  getCourseRepStudents: async (course: string): Promise<CourseRepStudent[]> => {
    try {
      const data = await apiFetch<unknown[]>(
        versioned(
          `/api/v1/course-rep/students?course=${encodeURIComponent(course)}`,
        ),
      );
      return data as CourseRepStudent[];
    } catch (error) {
      console.error("Failed to fetch students", error);
      return [];
    }
  },
  getCourseRepAnnouncements: async (
    course: string,
  ): Promise<CourseRepAnnouncement[]> => {
    try {
      const data = await apiFetch<unknown[]>(
        versioned(
          `/api/v1/course-rep/announcements/${encodeURIComponent(course)}`,
        ),
      );
      return data as CourseRepAnnouncement[];
    } catch (error) {
      console.error("Failed to fetch announcements", error);
      return [];
    }
  },

  sendTip: async (data: {
    student_id: string;
    message: string;
  }): Promise<{ status: string }> => {
    return apiFetch(versioned("/api/v1/course-rep/tip"), {
      method: "POST",
      body: JSON.stringify(data),
    });
  },
  createAnnouncement: async (data: {
    course_code: string;
    title: string;
    body: string;
    pinned?: boolean;
  }): Promise<void> => {
    return apiFetch(versioned("/api/v1/course-rep/announcements"), {
      method: "POST",
      body: JSON.stringify(data),
    });
  },
  updateAnnouncement: async (
    announcementId: string,
    data: { title?: string; body?: string; pinned?: boolean },
  ): Promise<void> => {
    return apiFetch(
      versioned(
        `/api/v1/course-rep/announcements/${encodeURIComponent(announcementId)}`,
      ),
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
    );
  },

  // Course Resources
  getCourseResources: async (
    courseId: string,
    params: {
      type?: string;
      week?: number;
      lecturer?: string;
      limit?: number;
      offset?: number;
    } = {},
  ): Promise<{ resources: Resource[]; total: number }> => {
    const searchParams = new URLSearchParams();
    if (params.type) searchParams.append("type", params.type);
    if (params.week) searchParams.append("week", params.week.toString());
    if (params.lecturer) searchParams.append("lecturer", params.lecturer);
    if (params.limit) searchParams.append("limit", params.limit.toString());
    if (params.offset) searchParams.append("offset", params.offset.toString());
    return apiFetch<{ resources: Resource[]; total: number }>(
      versioned(
        `/api/v1/courses/${encodeURIComponent(courseId)}/resources?${searchParams.toString()}`,
      ),
    ).then((res) => ({
      resources: res.resources,
      total: res.total,
    }));
  },

  // Study Groups
  getStudyGroups: async (course?: string): Promise<StudyGroup[]> => {
    try {
      const url = course
        ? versioned(`/api/v1/study-groups?course=${encodeURIComponent(course)}`)
        : versioned("/api/v1/study-groups");
      const data = await apiFetch<{ items: StudyGroup[]; total: number }>(url);
      const items = (data as any)?.items || [];
      return items.map((g: any) => ({
        id: g.id,
        name: g.name,
        course: g.course_code || g.course || "",
        course_code: g.course_code || g.course || "",
        description: g.description || "",
        members: g.member_count ?? g.members ?? 0,
        created_by: g.created_by || "",
        created_by_username: g.profiles?.username,
        is_member: g.is_member || false,
        is_locked: g.is_locked || false,
      }));
    } catch (error) {
      console.warn("Failed to fetch study groups", error);
      return [];
    }
  },
  uploadStatusVideo: async (
    file: File,
    caption = "",
  ): Promise<{ id: string; video_url: string; caption: string }> => {
    const form = new FormData();
    form.append("file", file);
    form.append("caption", caption);
    return apiFetch(versioned("/api/v1/status-videos/upload"), {
      method: "POST",
      body: form,
    });
  },
  getStatusVideos: async (
    limit = 30,
    offset = 0,
  ): Promise<{ videos: any[]; total: number }> => {
    return apiFetch(
      versioned(`/api/v1/status-videos?limit=${limit}&offset=${offset}`),
    );
  },
  reportStatusVideo: async (
    id: string,
    reason = "",
  ): Promise<{
    status: string;
    reports_count: number;
    confirmed_spam: boolean;
    bonus_awarded: number;
  }> => {
    return apiFetch(versioned(`/api/v1/status-videos/${id}/report`), {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  },

  // Tools
  getFormulaSheet: async (course: string): Promise<Formula[]> => {
    try {
      const data = await apiFetch<{ formulas: FormulaEntry[] }>(
        `/api/v1/tools/formula-sheet?course=${encodeURIComponent(course)}`,
      );
      const formulas: Formula[] = [];
      for (const f of (data as any)?.formulas ?? []) {
        formulas.push({
          id:
            f?.id ??
            `${f?.topic ?? "formula"}::${(data as any)?.formulas?.indexOf(f) ?? formulas.length}`,
          topic: f?.topic ?? "",
          latex: f?.formula_latex ?? "",
          description: f?.definition ?? "",
        });
      }
      return formulas;
    } catch (error) {
      console.warn("Failed to_fetch_formulas, using empty list", error);
      return [];
    }
  },
  getGlossary: async (course: string): Promise<GlossaryTerm[]> => {
    try {
      const data = await apiFetch<unknown>(
        `/api/v1/tools/glossary?course=${encodeURIComponent(course)}`,
      );
      const response = data as any;
      return (response.entries || []).map((e: unknown, index: number) => ({
        id: index.toString(),
        term: (e as any).topic,
        definition: (e as any).summary,
      }));
    } catch (error) {
      console.warn("Failed to_fetch_glossary, using empty list", error);
      return [];
    }
  },
  getFlashcards: async (
    course: string,
    opts?: { topic?: string; due?: boolean; limit?: number },
  ): Promise<Flashcard[]> => {
    try {
      const params = new URLSearchParams();
      params.set("course", course);
      if (opts?.topic) params.set("topic", opts.topic);
      if (opts?.due) params.set("due", "true");
      if (opts?.limit) params.set("limit", String(opts.limit));
      const data = await apiFetch<unknown>(
        `/api/v1/tools/flashcards?${params.toString()}`,
      );
      return ((data as any).cards || []).map((c: any, index: number) => ({
        id: c.id ?? index.toString(),
        question: c.front ?? c.question ?? "",
        answer: c.back ?? c.answer ?? "",
        front: c.front,
        back: c.back,
        topic: c.topic ?? "",
        difficulty: c.difficulty ?? 3,
        next_review: c.next_review ?? null,
        source: c.source ?? "qa_bank",
      }));
    } catch (error) {
      console.warn("Failed to_fetch_flashcards, using empty list", error);
      return [];
    }
  },
  getQuizQuestions: async (
    course: string,
    difficulty: string,
  ): Promise<QuizQuestion[]> => {
    return apiFetch(
      versioned(`/api/v1/tools/quiz?course=${course}&difficulty=${difficulty}`),
    );
  },
  getPointsShopItems: async (): Promise<ShopItem[]> => {
    return apiFetch(versioned("/api/v1/tools/points-shop"));
  },
  getWeeklyChallenge: async (): Promise<Challenge> => {
    return apiFetch(versioned("/api/v1/tools/challenge"));
  },
  getChallenges: async (): Promise<Challenge[]> => {
    return apiFetch(versioned("/api/v1/tools/challenges"));
  },
  getDailyChallenge: async (): Promise<{
    id: string;
    question: string;
    course_code: string;
    options: string[];
    correct_answer: string;
    date?: string;
    answered_today?: boolean;
    last_result?: {
      correct: boolean;
      correct_answer: string;
      bonus_granted: number;
    } | null;
  }> => {
    return apiFetch(versioned("/api/v1/tools/daily-challenge"));
  },
  submitDailyChallengeAnswer: async (params: {
    question_id: string;
    selected_option: string;
  }): Promise<{
    correct: boolean;
    correct_answer: string;
    bonus_granted: number;
    already_completed?: boolean;
  }> => {
    return apiFetch(versioned("/api/v1/tools/daily-challenge/answer"), {
      method: "POST",
      body: JSON.stringify(params),
    });
  },
  getQuests: async (): Promise<Quest[]> => {
    return apiFetch(versioned("/api/v1/user/quests"));
  },
  completeQuest: async (questId: string): Promise<void> => {
    return apiFetch(versioned(`/api/v1/user/quests/${questId}/complete`), {
      method: "POST",
    });
  },
  getLikelyQuestions: async (params: {
    course?: string;
    topic?: string;
    limit?: number;
    search?: string;
  }): Promise<{
    questions: Array<{
      id: string;
      question: string;
      topic: string;
      course_code: string;
      difficulty?: string;
      source?: string;
    }>;
    total: number;
  }> => {
    const p = new URLSearchParams();
    if (params.course) p.set("course", params.course);
    if (params.topic) p.set("topic", params.topic);
    if (params.limit) p.set("limit", String(params.limit));
    if (params.search) p.set("search", params.search);
    return apiFetch(
      versioned(`/api/v1/tools/likely-questions?${p.toString()}`),
    );
  },

  // Ask
  askQuestion: async (request: {
    question: string;
    course_code?: string;
    mode?:
      | "normal"
      | "exam"
      | "manual"
      | "stepwise"
      | "socratic"
      | "podcast"
      | "minimal"
      | "text_image"
      | "genius";
    conversation_id?: string;
    podcast?: boolean;
    podcast_duration?: number;
    command?: string;
    department?: string;
    level?: number;
    fast?: boolean;
    cache?: "auto" | "none";
    attachment?: {
      filename?: string;
      mime?: string;
      content?: string; // base64 file_bytes
      __________url?: string; // URL to fetch (only with use_web_context + server key)
    } | null;
    use_web_context?: boolean;
  }): Promise<AskResponse> => {
    const body: Record<string, unknown> = {
      question: request.question,
      course_code: request.course_code,
      mode: (request.mode || "direct").toLowerCase(),
      conversation_id: request.conversation_id || null,
      podcast: request.podcast ?? false,
      podcast_duration: request.podcast_duration ?? 30,
      command: request.command || null,
      department: request.department ?? null,
      level: request.level ?? null,
      fast: request.fast ?? false,
      cache: request.cache ?? "auto",
    };
    if (request.attachment) {
      body.attachment = request.attachment;
      body.use_web_context = Boolean(request.use_web_context);
    }
    return apiFetch(versioned("/ask"), {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  askQuestionStream: async (request: {
    question: string;
    course_code?: string;
    mode?:
      | "normal"
      | "exam"
      | "manual"
      | "stepwise"
      | "socratic"
      | "podcast"
      | "minimal"
      | "text_image"
      | "genius";
    conversation_id?: string;
    podcast?: boolean;
    podcast_duration?: number;
    command?: string;
    step_index?: number;
    stepwise_session?: string;
    cache?: "auto" | "none";
    signal?: AbortSignal;
    attachment?: {
      filename?: string;
      mime?: string;
      content?: string;
      __________url?: string;
    } | null;
    use_web_context?: boolean;
  }): Promise<ReadableStream> => {
    const {
      data: { session },
    } = supabase
      ? await supabase.auth.getSession()
      : { data: { session: null } };
    const token = session?.access_token || getAuthBearerToken();
    const headers: Record<string, string> = getBackendHeaders(token);
    headers["Content-Type"] = "application/json";
    const baseUrl = API_BASE_URL;
    const res = await fetch(`${baseUrl}${versioned("/ask/stream")}`, {
      method: "POST",
      headers,
      signal: request.signal,
      body: JSON.stringify({
        question: request.question,
        course_code: request.course_code,
        mode: (request.mode || "normal").toLowerCase(),
        conversation_id: request.conversation_id || null,
        podcast: request.podcast ?? false,
        podcast_duration: request.podcast_duration ?? 30,
        command: request.command || null,
        step_index: request.step_index ?? null,
        stepwise_session: request.stepwise_session || null,
        cache: request.cache ?? "auto",
        attachment: request.attachment || null,
        use_web_context: request.use_web_context || false,
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(
        typeof err.detail === "string"
          ? err.detail
          : typeof err.message === "string"
            ? err.message
            : `Stream error: ${res.status}`,
      );
    }
    const streamBody = res.body;
    if (!streamBody) {
      throw new Error("Stream response was empty.");
    }
    return streamBody as ReadableStream;
  },
  convertStepwise: async (request: {
    question: string;
    answer: string;
    course_code?: string;
    institution?: string;
    level?: number;
    semester?: string;
  }): Promise<AskResponse> => {
    const body: Record<string, unknown> = {
      question: request.question,
      answer: request.answer,
      course_code: request.course_code ?? null,
      institution: request.institution ?? null,
      level: request.level ?? null,
      semester: request.semester ?? null,
    };
    return apiFetch(versioned("/ask/convert-stepwise"), {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  createConversation: async (data?: {
    title?: string;
    course_code?: string;
  }): Promise<Conversation> => {
    return apiFetch(versioned("/api/v1/conversations"), {
      method: "POST",
      body: JSON.stringify({
        title: data?.title || "New Conversation",
        course_code: data?.course_code || "",
      }),
    });
  },
  listConversations: async (opts?: {
    limit?: number;
    archived?: boolean;
    pinned?: boolean;
    search?: string;
  }): Promise<Conversation[]> => {
    const pageSize = opts?.limit ?? 100;
    const all: Conversation[] = [];
    let offset = 0;
    try {
      // Paginate through every page so users with 100+ conversations still
      // see all of them (#5 — previously capped at the first page).
      for (let page = 0; page < 20; page++) {
        const params = new URLSearchParams();
        params.set("limit", String(pageSize));
        params.set("offset", String(offset));
        if (opts?.archived !== undefined)
          params.set("archived", String(opts.archived));
        if (opts?.pinned !== undefined)
          params.set("pinned", String(opts.pinned));
        if (opts?.search) params.set("search", opts.search);
        const data = await apiFetch<ConversationListResponse>(
          `${versioned("/api/v1/conversations")}?${params.toString()}`,
        );
        const batch = data.conversations || [];
        all.push(...batch);
        if (batch.length < pageSize) break;
        offset += pageSize;
      }
    } catch {
      // Fall back to whatever we managed to collect.
    }
    return all;
  },
  getConversation: async (conversationId: string): Promise<Conversation> => {
    return apiFetch(
      versioned("/api/v1/conversations/" + encodeURIComponent(conversationId)),
    );
  },
  getConversationMessages: async (
    conversationId: string,
  ): Promise<Message[]> => {
    return apiFetch(
      versioned(
        "/api/v1/messages/conversations/" +
          encodeURIComponent(conversationId) +
          "/messages",
      ),
    );
  },
  requestStepwise: async (
    conversationId: string,
    messageId: string,
    question: string,
    options?: { stepIndex?: number; stepwiseSession?: string | null },
  ): Promise<{
    answer: unknown;
    steps?: Array<{
      step?: number;
      title?: string | null;
      text?: string;
      verification?: Record<string, unknown> | null;
      visualisation?: AskResponse["visualisation"] | null;
    }>;
    stepwise_session?: string | null;
    stepwise_state?: {
      current_step?: number | null;
      total_steps?: number;
    } | null;
    // K.198 — step outline shown before revealing the steps.
    plan?: Array<{
      index: number;
      title: string;
      intent?: string | null;
    }> | null;
    // K.212 — adaptation signal for the currently-shown step.
    adaptation?: {
      signal?: string;
      suggest?: string;
      reasons?: string[];
      summary?: unknown;
    } | null;
    // K.199/200 — count of verified steps.
    verified_step_count?: number | null;
  }> => {
    return apiFetch(versioned("/api/v1/ask"), {
      method: "POST",
      body: JSON.stringify({
        question,
        mode: "stepwise",
        conversation_id: conversationId,
        course_code: "",
        step_index: options?.stepIndex ?? null,
        stepwise_session: options?.stepwiseSession ?? null,
      }),
    });
  },
  recordStepwiseInteraction: async (
    sessionId: string,
    stepIndex: number,
    event:
      | "hint"
      | "clue"
      | "retry"
      | "confused"
      | "jump_back"
      | "completed"
      | "note",
    detail?: string,
  ): Promise<{
    ok: boolean;
    session_id: string;
    step_index: number;
    event: string;
    summary: {
      hints: number;
      clues: number;
      retries: number;
      confused: boolean;
      completed: boolean;
      jump_backs: number;
      notes: number;
      last_event: string | null;
      last_event_ts: number | null;
    };
    adaptation: { signal: string; suggest: string } | null;
  }> => {
    return apiFetch(versioned("/api/v1/ask/stepwise/interaction"), {
      method: "POST",
      body: JSON.stringify({
        session_id: sessionId,
        step_index: stepIndex,
        event,
        detail: detail ?? null,
      }),
    });
  },
  submitFeedback: async (data: {
    question_id?: string;
    chunk_id?: string;
    rating: "positive" | "negative";
  }): Promise<{ status: string; rating: string }> => {
    return apiFetch(versioned("/api/v1/feedback"), {
      method: "POST",
      body: JSON.stringify(data),
    });
  },
  // Per-answer 1-10 quality rating (distinct from binary thumbs feedback).
  rateMessage: async (messageId: string, score: number): Promise<void> => {
    return apiFetch(
      versioned(`/api/v1/messages/${encodeURIComponent(messageId)}`),
      {
        method: "PUT",
        body: JSON.stringify({ rating: score }),
      },
    );
  },
  listPastQuestionsHub: async (params: {
    search?: string;
    course?: string;
    year?: string;
    difficulty?: string;
    topic?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ questions: PastQuestion[]; total: number }> => {
    const p = new URLSearchParams({
      limit: (params.limit || 20).toString(),
      offset: (params.offset || 0).toString(),
    });
    if (params.search) p.append("search", params.search);
    if (params.course) p.append("course", params.course);
    if (params.year) p.append("year", params.year);
    if (params.difficulty) p.append("difficulty", params.difficulty);
    if (params.topic) p.append("topic", params.topic);
    return apiFetch(versioned(`/api/v1/past-questions?${p.toString()}`));
  },
  // Persist an item to the user's saved vault. The backend `SavedItemRequest`
  // expects `item_type` + `item_id` (e.g. "note"|"flashcard"|"formula"|"answer"
  // |"question"). Callers must pass both so the save actually lands.
  savePost: async (
    itemType: string,
    itemId: string,
  ): Promise<string | undefined> => {
    const res = (await apiFetch(versioned("/api/v1/user/saved"), {
      method: "POST",
      body: JSON.stringify({ item_type: itemType, item_id: itemId }),
    })) as { id?: string };
    return res?.id;
  },
  unsavePost: async (postId: string): Promise<void> => {
    return apiFetch(
      versioned(`/api/v1/user/saved/${encodeURIComponent(postId)}`),
      {
        method: "DELETE",
      },
    );
  },
  saveToolData: async (toolType: string, data: unknown): Promise<void> => {
    return apiFetch(versioned(`/api/v1/tools/${toolType}/save`), {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  // Payments
  verifyPayment: async (
    transaction_ref: string,
    provider: string = "paystack",
  ): Promise<{ status: string; extra_credits: number }> => {
    // Backend exposes GET /api/v1/payments/verify/{reference} (user-scoped,
    // auto-detects Paystack/Monnify). The_reference_goes_in_the_path, not the body.
    return apiFetch(
      versioned(
        `/api/v1/payments/verify/${encodeURIComponent(transaction_ref)}`,
      ),
    );
  },
  initiatePayment: async (
    planId: string,
    promoCode?: string,
  ): Promise<{
    checkout_url: string;
    transaction_ref: string;
    monnify_reference: string;
    amount: number;
    original_amount: number;
    credits: number;
    promo_code?: string;
    discount_amount: number;
  }> => {
    return apiFetch(versioned("/api/v1/payments/initiate"), {
      method: "POST",
      body: JSON.stringify({ plan: planId, promo_code: promoCode || null }),
    });
  },
  getPaymentHistory: async (): Promise<{ payments: AdminPayment[] }> => {
    return apiFetch(versioned("/api/v1/payments/history"));
  },
  submitReceipt: async (
    tx_ref: string,
  ): Promise<{ status: string; message: string }> => {
    return apiFetch(versioned("/api/v1/payments/submit-receipt"), {
      method: "POST",
      body: JSON.stringify({ tx_ref }),
    });
  },
  getBillingInvoices: async (): Promise<Invoice[]> => {
    return apiFetch<Invoice[]>(versioned("/api/v1/billing/invoices"));
  },
  getPaymentMethods: async (): Promise<PaymentMethod[]> => {
    return apiFetch<PaymentMethod[]>(versioned("/api/v1/billing/methods"));
  },

  impersonateUser: async (
    userId: string,
    reason: string,
  ): Promise<{ impersonation_url: string }> => {
    return apiFetch(versioned("/api/v1/admin/impersonate"), {
      method: "POST",
      body: JSON.stringify({ user_id: userId, reason }),
    });
  },
  suspendUser: async (
    userId: string,
    durationHours: number = 24,
    reason?: string,
  ): Promise<void> => {
    const params = new URLSearchParams();
    params.set("duration_hours", String(durationHours));
    if (reason) params.set("reason", reason);
    return apiFetch(
      versioned(
        `/api/v1/admin/users/${encodeURIComponent(userId)}/suspend?${params.toString()}`,
      ),
      {
        method: "POST",
      },
    );
  },
  restoreUser: async (userId: string): Promise<void> => {
    return apiFetch(
      versioned(`/api/v1/admin/users/${encodeURIComponent(userId)}/restore`),
      {
        method: "POST",
      },
    );
  },
  banUser: async (userId: string, reason?: string): Promise<void> => {
    const params = reason ? `?reason=${encodeURIComponent(reason)}` : "";
    return apiFetch(
      versioned(
        `/api/v1/admin/users/${encodeURIComponent(userId)}/ban${params}`,
      ),
      {
        method: "POST",
      },
    );
  },

  // Transaction settlement (admin verifies / rejects payments)
  verifyTransaction: async (
    txId: string,
    action: "approve" | "reject",
  ): Promise<void> => {
    return apiFetch(
      versioned(
        `/api/v1/admin/transactions/${encodeURIComponent(txId)}/verify`,
      ),
      {
        method: "POST",
        body: JSON.stringify({ action }),
      },
    );
  },

  // Tutor oversight (approve / suspend a tutor application)
  updateTutorStatus: async (
    tutorId: string,
    status: "active" | "suspended" | "pending",
  ): Promise<void> => {
    return apiFetch(
      versioned(`/api/v1/admin/tutors/${encodeURIComponent(tutorId)}/status`),
      {
        method: "PATCH",
        body: JSON.stringify({ status }),
      },
    );
  },

  // Backup management (restore / delete / download)
  restoreBackup: async (backupId: string): Promise<void> => {
    return apiFetch(
      versioned(`/api/v1/admin/backup/${encodeURIComponent(backupId)}/restore`),
      {
        method: "POST",
      },
    );
  },
  deleteBackup: async (backupId: string): Promise<void> => {
    return apiFetch(
      versioned(`/api/v1/admin/backup/${encodeURIComponent(backupId)}`),
      {
        method: "DELETE",
      },
    );
  },
  downloadBackup: async (backupId: string): Promise<{ url: string }> => {
    return apiFetch(
      versioned(
        `/api/v1/admin/backup/${encodeURIComponent(backupId)}/download`,
      ),
    );
  },

  // Course Representative oversight (appointment / rejection / demotion)
  getCourseRepApplications: async (): Promise<User[]> => {
    return apiFetch<User[]>(
      versioned("/api/v1/admin/course-reps/applications"),
    );
  },
  getCourseReps: async (): Promise<User[]> => {
    return apiFetch<User[]>(versioned("/api/v1/admin/course-reps"));
  },
  decideCourseRep: async (
    userId: string,
    decision: "approve" | "reject",
    notes?: string,
  ): Promise<void> => {
    return apiFetch(
      versioned(
        `/api/v1/admin/course-reps/${encodeURIComponent(userId)}/decision`,
      ),
      {
        method: "POST",
        body: JSON.stringify({ decision, notes }),
      },
    );
  },
  demoteCourseRep: async (userId: string, notes?: string): Promise<void> => {
    return apiFetch(
      versioned(
        `/api/v1/admin/course-reps/${encodeURIComponent(userId)}/demote`,
      ),
      {
        method: "POST",
        body: JSON.stringify({ notes }),
      },
    );
  },

  // Content moderation (admin deletes platform-generated content)
  deleteMaterial: async (materialId: string): Promise<void> => {
    return apiFetch(
      versioned(`/api/v1/admin/materials/${encodeURIComponent(materialId)}`),
      {
        method: "DELETE",
      },
    );
  },
  deleteThread: async (threadId: string): Promise<void> => {
    return apiFetch(
      versioned(
        `/api/v1/admin/community/threads/${encodeURIComponent(threadId)}`,
      ),
      {
        method: "DELETE",
      },
    );
  },
  deleteAnnouncement: async (announcementId: string): Promise<void> => {
    return apiFetch(
      versioned(
        `/api/v1/admin/announcements/${encodeURIComponent(announcementId)}`,
      ),
      {
        method: "DELETE",
      },
    );
  },
  deleteStudyGroup: async (groupId: string): Promise<void> => {
    return apiFetch(
      versioned(`/api/v1/admin/study-groups/${encodeURIComponent(groupId)}`),
      {
        method: "DELETE",
      },
    );
  },

  // Content reporting (public users flag violating content for admin review)
  reportContent: async (
    targetType: "thread" | "material" | "announcement" | "study_group",
    targetId: string,
    reason: string,
  ): Promise<{ status: string; reports_count?: number }> => {
    return apiFetch(versioned("/api/v1/reports"), {
      method: "POST",
      body: JSON.stringify({
        target_type: targetType,
        target_id: targetId,
        reason,
      }),
    });
  },

  // Admin report queue
  getReports: async (): Promise<
    Array<{
      id: string;
      target_type: string;
      target_id: string;
      reason: string;
      reporter_id?: string;
      created_at: string;
      resolved?: boolean;
    }>
  > => {
    return apiFetch(versioned("/api/v1/admin/reports"));
  },
  resolveReport: async (reportId: string): Promise<void> => {
    return apiFetch(
      versioned(
        `/api/v1/admin/reports/${encodeURIComponent(reportId)}/resolve`,
      ),
      {
        method: "POST",
      },
    );
  },
  deleteAccount: async (password: string): Promise<{ message: string }> => {
    return apiFetch(versioned("/api/v1/user/account"), {
      method: "DELETE",
      body: JSON.stringify({ password }),
    });
  },
  // User Badges
  getUserBadges: async (): Promise<AchievementBadge[]> => {
    return apiFetch(versioned("/api/v1/user/badges")) as Promise<
      AchievementBadge[]
    >;
  },
  getIdentity: async (): Promise<UserIdentity> => {
    return apiFetch(
      versioned("/api/v1/user/identity"),
    ) as Promise<UserIdentity>;
  },
  getSettings: async (): Promise<{
    notifications: {
      email: boolean;
      inApp: boolean;
      replies: boolean;
      payments: boolean;
      approvals: boolean;
    };
    language: string;
  }> => {
    return apiFetch(versioned("/api/v1/user/settings"));
  },
  updateSettings: async (
    settings: Partial<UserSettings> & {
      notificationId?: string;
      read?: boolean;
    },
  ): Promise<void> => {
    return apiFetch(versioned("/api/v1/user/settings"), {
      method: "PATCH",
      body: JSON.stringify(settings),
    });
  },
  markNotificationRead: async (id: string): Promise<void> => {
    return apiFetch(versioned(`/api/v1/notifications/${id}/read`), {
      method: "POST",
    });
  },
  markAllNotificationsRead: async (): Promise<void> => {
    return apiFetch(versioned("/api/v1/notifications/read-all"), {
      method: "POST",
    });
  },
  getNotificationHistory: async (
    page: number = 1,
    limit: number = 20,
  ): Promise<UserNotification[]> => {
    const offset = (page - 1) * limit;
    const data = await apiFetch<any[]>(
      versioned(`/api/v1/notifications?limit=${limit}&offset=${offset}`),
    );
    return ((data as any[]) || []).map((n: any) => ({
      id: n.id || n.notification_id || "",
      message: n.body || n.message || n.title || "",
      timestamp: n.created_at || n.createdAt || new Date().toISOString(),
      icon: n.icon || n.type || "bell",
      isRead:
        n.read_at != null || n.read === true || n.isRead === true || false,
      type: (n.type as "info" | "alert" | "success" | undefined) || "info",
      link: n.data?.link || n.link,
    })) as UserNotification[];
  },
  // Exam Campaign
  startExamCampaign: async (
    examDate?: string,
  ): Promise<{ status: string; exam_date: string; days_remaining: number }> => {
    const body: any = {};
    if (examDate) body.exam_campaign_exam_date = examDate;
    return apiFetch(versioned("/api/v1/notifications/exam-campaign/start"), {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  stopExamCampaign: async (): Promise<{ status: string }> => {
    return apiFetch(versioned("/api/v1/notifications/exam-campaign/stop"), {
      method: "POST",
    });
  },
  getExamCampaignStatus: async (): Promise<{
    enabled: boolean;
    exam_date: string;
    days_remaining: number;
    current_day: number;
    last_sent_day?: string;
  }> => {
    return apiFetch(versioned("/api/v1/notifications/exam-campaign/status"));
  },
  // Course Feedback
  updateCourseFeedback: async (
    courseId: string,
    feedbackId: string,
    data: { rating?: number; review_text?: string },
  ): Promise<void> => {
    return apiFetch(
      versioned(
        "/api/v1/courses/" +
          encodeURIComponent(courseId) +
          "/feedback/" +
          feedbackId,
      ),
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
    );
  },
  submitCourseFeedback: async (
    courseId: string,
    data: { rating: number; review_text: string },
  ): Promise<void> => {
    return apiFetch(
      versioned(
        "/api/v1/courses/" + encodeURIComponent(courseId) + "/feedback",
      ),
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
  },
  markFeedbackHelpful: async (
    courseId: string,
    feedbackId: string,
  ): Promise<void> => {
    return apiFetch(
      versioned(
        "/api/v1/courses/" +
          encodeURIComponent(courseId) +
          "/feedback/" +
          feedbackId +
          "/helpful",
      ),
      { method: "POST" },
    );
  },
  // Past Questions
  getGlobalPastQuestionStats: async (): Promise<unknown> => {
    return apiFetch(versioned("/api/v1/past-questions/stats"));
  },
  recordPastQuestionResult: async (
    questionId: string,
    isCorrect: boolean,
  ): Promise<void> => {
    return apiFetch(versioned("/api/v1/user/past-question-result"), {
      method: "POST",
      body: JSON.stringify({ question_id: questionId, correct: isCorrect }),
    });
  },
  // Course Syllabus
  getCourseSyllabus: async (courseId: string): Promise<unknown> => {
    return apiFetch(
      versioned(
        "/api/v1/courses/" + encodeURIComponent(courseId) + "/syllabus",
      ),
    );
  },
  // Maintenance
  getMaintenanceStatus: async (): Promise<{
    enabled: boolean;
    message?: string;
  }> => {
    return apiFetch(versioned("/api/v1/admin/maintenance"));
  },
  setMaintenanceMode: async (
    enabled: boolean,
    message?: string,
  ): Promise<{ status: string; enabled: boolean; message?: string }> => {
    return apiFetch(versioned("/api/v1/admin/maintenance"), {
      method: "POST",
      body: JSON.stringify({ enabled, message }),
    });
  },
  // Departments
  // Departments
  getDepartments: async (): Promise<{ name: string; code: string }[]> => {
    return apiFetch(versioned("/api/v1/departments"));
  },
  // Study Plan
  getStudyPlan: async (): Promise<
    { id: string; topic: string; completed: boolean }[]
  > => {
    return apiFetch(versioned("/api/v1/user/study-plan"));
  },
  updateStudyPlanItem: async (
    itemId: string,
    completed: boolean,
  ): Promise<void> => {
    return apiFetch(
      versioned("/api/v1/user/study-plan/" + encodeURIComponent(itemId)),
      {
        method: "PATCH",
        body: JSON.stringify({ completed }),
      },
    );
  },
  getActivity: async (limit: number): Promise<unknown[]> => {
    return apiFetch(versioned("/api/v1/user/activity?limit=" + limit));
  },
  // Readiness
  getReadiness: async (): Promise<{
    overall: number;
    courses: Record<string, number>;
    readiness: { course_code: string; course_name: string; score: number }[];
  }> => {
    return apiFetch(versioned("/api/v1/user/readiness"));
  },
  getWeakTopics: async (): Promise<WeakTopic[]> => {
    return apiFetch(versioned("/api/v1/tools/weak-topics"));
  },
  getAccuracyTrend: async (
    days: number,
  ): Promise<{ date: string; accuracy: number }[]> => {
    return apiFetch(versioned("/api/v1/user/accuracy-trend?days=" + days));
  },
  // Sessions
  getActiveSessions: async (): Promise<AuthSession[]> => {
    return apiFetch(versioned("/api/v1/user/sessions/active"));
  },
  revokeSession: async (sessionId: string): Promise<void> => {
    return apiFetch(
      versioned(
        "/api/v1/user/sessions/" + encodeURIComponent(sessionId) + "/revoke",
      ),
      {
        method: "POST",
      },
    );
  },
  listSessions: async (role: string): Promise<Session[]> => {
    return apiFetch(versioned("/api/v1/sessions/" + role));
  },
  // Study Group Messaging
  sendMessage: async (groupId: string, message: string): Promise<void> => {
    return apiFetch(
      versioned(
        "/api/v1/study-groups/" + encodeURIComponent(groupId) + "/messages",
      ),
      {
        method: "POST",
        body: JSON.stringify({ body: message }),
      },
    );
  },
  /**
   * Send an end-to-end encrypted message. The caller has already encrypted the
   * body client-side using the e2e_crypto utility and receives back the
   * encrypted payload fields.
   */
  sendEncryptedMessage: async (
    groupId: string,
    payload: { body_encrypted?: string; e2e_iv?: string; e2e_key_id?: string },
  ): Promise<void> => {
    return apiFetch(
      versioned(
        "/api/v1/study-groups/" + encodeURIComponent(groupId) + "/messages",
      ),
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    );
  },
  removeStudyGroupMember: async (
    groupId: string,
    memberId: string,
  ): Promise<void> => {
    return apiFetch(
      versioned(
        "/api/v1/study-groups/" +
          encodeURIComponent(groupId) +
          "/members/" +
          encodeURIComponent(memberId),
      ),
      { method: "DELETE" },
    );
  },
  // Tutor
  getTutorProfile: async (
    tutorId: string,
  ): Promise<Tutor & { sessions: Session[] }> => {
    return apiFetch(versioned("/api/v1/tutors/" + encodeURIComponent(tutorId)));
  },
  requestSession: async (
    tutorId: string,
    data: { start: string; end: string },
  ): Promise<void> => {
    return apiFetch(
      versioned("/api/v1/tutors/" + encodeURIComponent(tutorId) + "/request"),
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
  },
  requestTutorSession: async (
    tutorId: string,
    slotId: string,
  ): Promise<void> => {
    return apiFetch(
      versioned("/api/v1/tutors/" + encodeURIComponent(tutorId) + "/book"),
      {
        method: "POST",
        body: JSON.stringify({ slotId }),
      },
    );
  },
  // Document
  getDocument: async (
    docId: string,
  ): Promise<{ id: string; title: string; content: string }> => {
    return apiFetch(
      versioned("/api/v1/documents/" + encodeURIComponent(docId)),
    );
  },
  // Payments
  getPaymentStatus: async (
    txRef: string,
  ): Promise<{ status: string; verified: boolean }> => {
    // Backend: POST /api/v1/payments/status/{transaction_ref}
    return apiFetch(
      versioned("/api/v1/payments/status/" + encodeURIComponent(txRef)),
      {
        method: "POST",
      },
    );
  },
  verifyMonnifyPayment: async (reference: string): Promise<unknown> => {
    // Backend: GET /api/v1/payments/verify/{reference} (user-scoped).
    return apiFetch(
      versioned(`/api/v1/payments/verify/${encodeURIComponent(reference)}`),
    );
  },
  // Promo
  validatePromo: async (payload: {
    code: string;
    purchase_type: string;
    purchase_amount: number;
  }): Promise<{
    valid: boolean;
    reason?: string;
    discount_type?: string;
    discount_value?: number;
    original_amount?: number;
    discount_amount?: number;
    final_amount?: number;
    benefit_description?: string;
    promo_code_id?: string;
    promo_code?: string;
  }> => {
    return apiFetch(versioned("/api/v1/promo/validate"), {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  redeemPromo: async (payload: {
    code: string;
    purchase_type: string;
    purchase_amount_original: number;
    purchase_amount_discounted: number;
    monnify_transaction_ref?: string;
  }): Promise<{
    status: string;
    credits_added: number;
    new_balance: number;
    benefit_description: string;
  }> => {
    return apiFetch(versioned("/api/v1/promo/redeem"), {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  getPromoRedemptions: async (): Promise<{
    redemptions: Array<{
      id: string;
      code: string;
      discount_type: string;
      applied_to: string;
      benefit_description: string;
      discount_amount: number | null;
      bonus_questions: number | null;
      bonus_days: number | null;
      monnify_transaction_ref: string | null;
      redeemed_at: string | null;
    }>;
  }> => {
    return apiFetch(versioned("/api/v1/promo/redemptions"));
  },
  // Admin promo CRUD
  createPromoCode: async (payload: {
    code: string;
    description?: string;
    discount_type: string;
    discount_value: number;
    applies_to?: string;
    max_uses?: number | null;
    min_purchase_amount?: number;
    expires_at?: string | null;
    is_active?: boolean;
  }): Promise<Record<string, unknown>> => {
    return apiFetch(versioned("/api/v1/admin/promo-codes"), {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  getAdminPromoCodes: async (opts?: {
    active?: boolean;
    search?: string;
  }): Promise<{
    promo_codes: Array<Record<string, unknown>>;
  }> => {
    const params = new URLSearchParams();
    if (opts?.active !== undefined) params.set("active", String(opts.active));
    if (opts?.search) params.set("search", opts.search);
    const qs = params.toString();
    return apiFetch(
      versioned("/api/v1/admin/promo-codes" + (qs ? `?${qs}` : "")),
    );
  },
  updatePromoCode: async (
    promoId: string,
    payload: Record<string, unknown>,
  ): Promise<Record<string, unknown>> => {
    return apiFetch(
      versioned("/api/v1/admin/promo-codes/" + encodeURIComponent(promoId)),
      {
        method: "PATCH",
        body: JSON.stringify(payload),
      },
    );
  },
  deletePromoCode: async (promoId: string): Promise<{ status: string }> => {
    return apiFetch(
      versioned("/api/v1/admin/promo-codes/" + encodeURIComponent(promoId)),
      {
        method: "DELETE",
      },
    );
  },
  getPromoStats: async (): Promise<{
    total_codes: number;
    active_codes: number;
    total_redemptions: number;
    total_discount_amount: number;
    total_bonus_questions: number;
    total_bonus_days: number;
  }> => {
    return apiFetch(versioned("/api/v1/admin/promo-codes/stats"));
  },
  // Terms
  acceptTerms: async (): Promise<{
    status: string;
    terms_accepted_at: string;
  }> => {
    return apiFetch(versioned("/api/v1/user/accept-terms"), {
      method: "POST",
      body: JSON.stringify({ accepted: true }),
    });
  },
  getTermsStatus: async (): Promise<{
    accepted: boolean;
    terms_accepted_at?: string;
  }> => {
    return apiFetch(versioned("/api/v1/user/terms-status"));
  },
  // Events

  getPopularTopics: async (): Promise<WeakTopic[]> => {
    return apiFetch(versioned("/api/v1/past-questions/popular-topics"));
  },
  getAllEvents: async (): Promise<AppEvent[]> => {
    return apiFetch(versioned("/api/v1/events/all"));
  },
  // Export
  getExportHistory: async (): Promise<
    { id: string; date: string; status: string }[]
  > => {
    return apiFetch(versioned("/api/v1/user/export-history"));
  },
  // Misc
  listThreads: async (course?: string): Promise<Thread[]> => {
    try {
      const data = await apiFetch<any[]>(
        `/api/v1/community/threads${course ? `?course=${encodeURIComponent(course)}` : ""}`,
      );
      return data.map((t) => ({
        id: (t as any).id,
        title: (t as any).title,
        body: (t as any).body,
        author: {
          id: (t as any).user_id || (t as any).author_id || "",
          name:
            (t as any).profiles?.full_name ||
            (t as any).profiles?.username ||
            "Anonymous",
          username: (t as any).profiles?.username,
          avatar: (t as any).profiles?.avatar_url,
          initials: (
            (t as any).profiles?.full_name ||
            (t as any).profiles?.username ||
            "A"
          ).charAt(0),
        },
        createdAt: (t as any).created_at,
        tag: (t as any).course || "General",
        is_pinned: (t as any).is_pinned,
        course_code: (t as any).course_code,
      }));
    } catch (error) {
      console.warn("Failed to fetch threads", error);
      return [];
    }
  },

  getCommunityMembers: async (level: number): Promise<number> => {
    try {
      const data = await apiFetch<{ level: number; count: number }>(
        `/api/v1/community/members?level=${encodeURIComponent(level)}`,
      );
      return data?.count ?? 0;
    } catch (error) {
      console.warn("Failed to fetch community member count", error);
      return 0;
    }
  },

  getCommunities: async (): Promise<Community[]> => {
    try {
      const data = await apiFetch<{ items: Community[]; total: number }>(
        versioned("/api/v1/communities"),
      );
      return Array.isArray(data?.items) ? data.items : [];
    } catch (error) {
      console.warn("Failed to fetch communities", error);
      return [];
    }
  },

  createCommunity: async (payload: {
    name: string;
    description?: string;
    course_code: string;
    level: number;
  }): Promise<any> => {
    return apiFetch(versioned("/api/v1/communities"), {
      method: "POST",
      body: JSON.stringify({
        name: payload.name,
        description: payload.description || "",
        course_code: payload.course_code,
        level: payload.level,
      }),
    });
  },

  // Create a community thread. Also the fallback used by official rooms that
  // are addressed by slug (e.g. /community/futo-200-level): the backend only
  // accepts UUID thread ids, so the room needs a real thread to reply to.
  createCommunityThread: async (payload: {
    title: string;
    body: string;
    course_code?: string;
    images?: string[];
  }): Promise<{
    id: string;
    title?: string;
    course_code?: string;
    images?: string[];
  }> => {
    return apiFetch(versioned("/api/v1/community/threads"), {
      method: "POST",
      body: JSON.stringify({
        title: payload.title,
        body: payload.body,
        course_code: payload.course_code || null,
        images: payload.images || [],
      }),
    });
  },

  // Community moderation endpoints — course reps and admins only
  deleteCommunityThread: async (threadId: string): Promise<void> => {
    return apiFetch(
      versioned(`/api/v1/community/threads/${encodeURIComponent(threadId)}`),
      {
        method: "DELETE",
      },
    );
  },
  pinCommunityThread: async (
    threadId: string,
    pinned: boolean = true,
  ): Promise<void> => {
    return apiFetch(
      versioned(
        `/api/v1/community/threads/${encodeURIComponent(threadId)}/pin?pinned=${pinned}`,
      ),
      {
        method: "PATCH",
      },
    );
  },
  deleteCommunityReply: async (
    threadId: string,
    replyId: string,
  ): Promise<void> => {
    return apiFetch(
      versioned(
        `/api/v1/community/threads/${encodeURIComponent(threadId)}/replies/${encodeURIComponent(replyId)}`,
      ),
      {
        method: "DELETE",
      },
    );
  },

  getStudyGroup: async (groupId: string): Promise<StudyGroup> => {
    return apiFetch(versioned(`/api/v1/study-groups/${encodeURIComponent(groupId)}`));
  },

  joinStudyGroup: async (groupId: string): Promise<void> => {
    return apiFetch(
      versioned(`/api/v1/study-groups/${encodeURIComponent(groupId)}/join`),
      { method: "POST" },
    );
  },

  leaveStudyGroup: async (groupId: string): Promise<void> => {
    return apiFetch(
      versioned(`/api/v1/study-groups/${encodeURIComponent(groupId)}/leave`),
      { method: "POST" },
    );
  },

  joinCommunity: async (communityId: string): Promise<void> => {
    return apiFetch(
      versioned(`/api/v1/communities/${encodeURIComponent(communityId)}/join`),
      { method: "POST" },
    );
  },

  leaveCommunity: async (communityId: string): Promise<void> => {
    return apiFetch(
      versioned(`/api/v1/communities/${encodeURIComponent(communityId)}/leave`),
      { method: "POST" },
    );
  },

  // -------------------------------------------------------------------------
  // Public page content (admin-editable copy)
  // -------------------------------------------------------------------------
  getPageContent: async <T>(
    page: string,
  ): Promise<{ page: string; data: T | null; updated_at: string | null }> =>
    apiFetch(versioned(`/api/v1/content/${page}`)),

  updatePageContent: async (page: string, data: Record<string, unknown>) =>
    apiFetch(versioned(`/api/v1/content/${page}`), {
      method: "PUT",
      body: JSON.stringify({ data }),
    }),

  redeemPoints: async (itemId: string): Promise<void> => {
    return apiFetch(versioned("/api/v1/points/redeem"), {
      method: "POST",
      body: JSON.stringify({ item_id: itemId }),
    });
  },

  // =========================================================================
  // Courses (public catalog + user_enrollment, adapted to existing backend)
  // =========================================================================
  // Backend: GET /api/v1/courses returns string[] of course codes.
  // Frontend enriches via materials table to build Course objects.
  getCoursesList: async (params?: {
    search?: string;
    department?: string;
    level?: string;
  }): Promise<string[]> => {
    const p = new URLSearchParams();
    if (params?.search) p.set("search", params.search);
    if (params?.level) p.set("level", params.level);
    // Backend doesn't support ?department=, so filter client-side if provided
    const qs = p.toString();
    const codes: string[] = await apiFetch(
      versioned("/api/v1/courses" + (qs ? `?${qs}` : "")),
    );
    return codes;
  },

  // User courses are stored in the profiles.courses_field.
  // Backend: PATCH /api/v1/user/profile accepts { courses: [...] }.
  // Enforces MAX_COURSES = 15 server-side.
  getUserCourses: async (): Promise<string[]> => {
    try {
      const profile = await apiFetch<Record<string, unknown>>(
        versioned("/api/v1/user/profile"),
      );
      const courses = (profile as any)?.courses;
      return Array.isArray(courses) ? courses : [];
    } catch {
      return [];
    }
  },
  setUserCourses: async (courses: string[]): Promise<void> => {
    await apiFetch(versioned("/api/v1/user/profile"), {
      method: "PATCH",
      body: JSON.stringify({ courses }),
    });
  },
  bulkAddCourses: async (courseIds: string[]): Promise<void> => {
    await apiFetch(versioned("/api/v1/user/profile"), {
      method: "PATCH",
      body: JSON.stringify({ courses: courseIds }),
    });
  },
  removeCourse: async (courseId: string): Promise<void> => {
    try {
      const current = await api.getUserCourses();
      const filtered = current.filter((c) => c !== courseId);
      await apiFetch(versioned("/api/v1/user/profile"), {
        method: "PATCH",
        body: JSON.stringify({ courses: filtered }),
      });
    } catch (error) {
      throw error;
    }
  },
  exchangeCourse: async (removeId: string, addId: string): Promise<void> => {
    const current = await api.getUserCourses();
    const swapped = current.filter((c) => c !== removeId);
    swapped.push(addId);
    await apiFetch(versioned("/api/v1/user/profile"), {
      method: "PATCH",
      body: JSON.stringify({ courses: swapped }),
    });
  },
  completeOnboarding: async (): Promise<void> => {
    await apiFetch(versioned("/api/v1/user/profile"), {
      method: "PATCH",
      body: JSON.stringify({ onboarding_completed: true }),
    });
  },

  // =========================================================================
  // Referrals
  // =========================================================================
  getMyReferralCode: async (): Promise<{
    code: string | null;
    expires_at: string | null;
  }> => {
    return apiFetch(versioned("/api/v1/referrals/my-code"));
  },
  generateReferralCode: async (): Promise<{
    code: string | null;
    expires_at: string | null;
  }> => {
    return apiFetch(versioned("/api/v1/referrals/generate"), {
      method: "POST",
    });
  },
  getReferralStats: async (): Promise<{
    referral_code: string | null;
    total_referred: number;
    rewards_earned: number;
  }> => {
    return apiFetch(versioned("/api/v1/referrals/stats"));
  },
  applyReferralCode: async (
    code: string,
  ): Promise<{
    status: string;
    credits_added: number;
    extra_credits: number;
  }> => {
    return apiFetch(versioned("/api/v1/referrals/apply"), {
      method: "POST",
      body: JSON.stringify({ code }),
    });
  },

  // =========================================================================
  // Admin Dashboard
  // =========================================================================
  // Backend: GET /api/v1/admin/stats returns { totalUsers, activeUsers,
  //   questionsAskedToday, revenue, dau, wau, mau, ... }
  getAdminDashboardStats: async (): Promise<AdminDashboardStats> => {
    return apiFetch(versioned("/api/v1/admin/stats"));
  },
  getAdminRevenue: async (days: number = 30): Promise<AdminRevenueData[]> => {
    return apiFetch(versioned(`/api/v1/admin/revenue?days=${days}`));
  },
  getAdminHealth: async (): Promise<AdminHealthReport> => {
    return apiFetch(versioned("/api/v1/admin/health"));
  },
  getAdminRecentErrors: async (limit: number = 20): Promise<unknown[]> => {
    return apiFetch(versioned(`/api/v1/admin/errors?limit=${limit}`));
  },
  getAdminRecentActivity: async (limit: number = 20): Promise<unknown[]> => {
    return apiFetch(versioned(`/api/v1/admin/activity?limit=${limit}`));
  },
  addCredits: async (userId: string, amount: number): Promise<void> => {
    await apiFetch(
      versioned(`/api/v1/admin/users/${encodeURIComponent(userId)}/quota`),
      {
        method: "POST",
        body: JSON.stringify({ extra_questions: amount }),
      },
    );
  },
  getAdminTransactions: async (params?: {
    status?: string;
    date?: string;
    limit?: number;
    offset?: number;
  }): Promise<AdminTransaction[]> => {
    const p = new URLSearchParams();
    if (params?.status) p.set("status", params.status);
    if (params?.limit) p.set("limit", String(params.limit));
    if (params?.offset) p.set("offset", String(params.offset));
    const qs = p.toString();
    return apiFetch(
      versioned(`/api/v1/admin/transactions${qs ? `?${qs}` : ""}`),
    );
  },

  // =========================================================================
  // Tickets (user + admin, backed by /api/v1/tickets)
  // =========================================================================
  createTicket: async (data: {
    subject: string;
    description: string;
    priority?: string;
  }): Promise<Ticket> => {
    return apiFetch(versioned("/api/v1/tickets"), {
      method: "POST",
      body: JSON.stringify(data),
    });
  },
  listMyTickets: async (): Promise<Ticket[]> => {
    return apiFetch(versioned("/api/v1/tickets"));
  },
  getTicket: async (ticketId: string): Promise<TicketDetail> => {
    return apiFetch(
      versioned(`/api/v1/tickets/${encodeURIComponent(ticketId)}`),
    );
  },
  addTicketMessage: async (ticketId: string, body: string): Promise<void> => {
    await apiFetch(
      versioned(`/api/v1/tickets/${encodeURIComponent(ticketId)}/messages`),
      {
        method: "POST",
        body: JSON.stringify({ body }),
      },
    );
  },
  // Admin ticket management (from tickets_router admin endpoints)
  adminListTickets: async (params?: {
    status?: string;
    priority?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<Ticket[]> => {
    const p = new URLSearchParams();
    if (params?.status && params.status !== "all")
      p.set("status", params.status);
    if (params?.priority && params.priority !== "all")
      p.set("priority", params.priority);
    if (params?.limit) p.set("limit", String(params.limit));
    if (params?.offset) p.set("offset", String(params.offset));
    const qs = p.toString();
    return apiFetch(versioned(`/api/v1/tickets/admin${qs ? `?${qs}` : ""}`));
  },
  adminUpdateTicket: async (
    ticketId: string,
    data: { status?: string; priority?: string; assigned_to?: string | null },
  ): Promise<void> => {
    await apiFetch(
      versioned(`/api/v1/tickets/admin/${encodeURIComponent(ticketId)}`),
      {
        method: "PATCH",
        body: JSON.stringify(data),
      },
    );
  },
  adminReplyTicket: async (ticketId: string, body: string): Promise<void> => {
    await apiFetch(
      versioned(`/api/v1/tickets/admin/${encodeURIComponent(ticketId)}/reply`),
      {
        method: "POST",
        body: JSON.stringify({ body }),
      },
    );
  },

  // =========================================================================
  // Admin Config & Settings
  // =========================================================================
  // Backend: GET /api/v1/config (remapped from /admin/config via PATH_REMAPS)
  getConfig: async (): Promise<Record<string, unknown>> => {
    return apiFetch(versioned("/api/v1/admin/config"));
  },
  updateConfig: async (key: string, value: unknown): Promise<void> => {
    await apiFetch(
      versioned(`/api/v1/admin/config/${encodeURIComponent(key)}`),
      {
        method: "PATCH",
        body: JSON.stringify({ value }),
      },
    );
  },
  getPlatformSettings: async (): Promise<Record<string, unknown>> => {
    const res = await apiFetch<{ settings: Record<string, unknown> }>(
      versioned("/api/v1/admin/platform-settings"),
    );
    return res.settings || {};
  },
  updatePlatformSetting: async (key: string, value: unknown): Promise<void> => {
    await apiFetch(
      versioned(`/api/v1/admin/platform-settings/${encodeURIComponent(key)}`),
      {
        method: "PUT",
        body: JSON.stringify({ value }),
      },
    );
  },

  // =========================================================================
  // Support (faculty contact)
  // =========================================================================
  contactSupport: async (data: {
    subject: string;
    details: string;
  }): Promise<{ status: string; request_id: string }> => {
    return apiFetch(versioned("/api/v1/support/contact"), {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  // -- Admin: Backup ----------------------------------------------
  getBackups: async (): Promise<{ backups: Backup[] }> => {
    return apiFetch(versioned("/api/v1/admin/backup"));
  },

  // -- Admin: Cache -----------------------------------------------
  getAdminCache: async (): Promise<unknown> => {
    return apiFetch(versioned("/api/v1/admin/cache"));
  },
  clearAdminCache: async (): Promise<{
    status: string;
    message?: string;
    result?: unknown;
  }> => {
    return apiFetch(versioned("/api/v1/admin/cache/clear"), { method: "POST" });
  },
  optimizeAdminCache: async (): Promise<{
    status: string;
    message?: string;
    result?: unknown;
  }> => {
    return apiFetch(versioned("/api/v1/admin/cache/optimize"), {
      method: "POST",
    });
  },

  // -- Admin: Users -----------------------------------------------
  getAdminUsers: async (
    params: Record<string, unknown> = {},
  ): Promise<{ users: User[]; total: number }> => {
    const qs = new URLSearchParams(
      Object.entries(params).map(([k, v]) => [k, String(v)]),
    );
    return apiFetch(versioned(`/api/v1/admin/users?${qs}`));
  },
  updateUserRole: async (
    userId: string,
    role: string,
  ): Promise<{ status: string }> => {
    return apiFetch(versioned(`/api/v1/admin/users/${userId}/role`), {
      method: "POST",
      body: JSON.stringify({ role }),
    });
  },
  getPublicProfile: async (username: string): Promise<unknown> => {
    return apiFetch(versioned(`/api/v1/u/${username}`));
  },

  // -- Admin: Logs ------------------------------------------------
  getAdminLogs: async (
    params: Record<string, unknown> = {},
  ): Promise<{ logs: unknown[] }> => {
    const qs = new URLSearchParams(
      Object.entries(params).map(([k, v]) => [k, String(v)]),
    );
    return apiFetch(versioned(`/api/v1/admin/logs?${qs}`));
  },
  getErrorLogs: async (limit: number = 50): Promise<{ errors: unknown[] }> => {
    return apiFetch(versioned(`/api/v1/admin/logs/errors?limit=${limit}`));
  },

  // -- Admin: Stats -----------------------------------------------
  getAdminStats: async (): Promise<unknown> => {
    return apiFetch(versioned("/api/v1/admin/stats"));
  },
  getKnowledgeHealth: async (): Promise<unknown> => {
    return apiFetch(versioned("/api/v1/admin/knowledge-health"));
  },
  getSystemStatus: async (): Promise<unknown> => {
    return apiFetch(versioned("/api/v1/admin/status"));
  },
  getUptimeMonitors: async (): Promise<{ monitors: unknown[] }> => {
    return apiFetch(versioned("/api/v1/admin/uptime-monitors"));
  },
  getAdminProviders: async (): Promise<{ providers: unknown[] }> => {
    return apiFetch(versioned("/api/v1/admin/providers"));
  },

  // -- Admin: Payments / Tutors / Transactions --------------------
  getAdminPayments: async (
    params: Record<string, unknown> = {},
  ): Promise<{ payments: AdminPayment[]; total: number }> => {
    const qs = new URLSearchParams(
      Object.entries(params).map(([k, v]) => [k, String(v)]),
    );
    return apiFetch(versioned(`/api/v1/admin/transactions?${qs}`));
  },
  getAdminTutors: async (): Promise<{ tutors: unknown[] }> => {
    return apiFetch(versioned("/api/v1/admin/tutors"));
  },

  // -- Admin: Email templates -------------------------------------
  getEmailTemplates: async (): Promise<{ templates: unknown[] }> => {
    return apiFetch(versioned("/api/v1/admin/email-templates"));
  },

  // -- Admin: Security --------------------------------------------
  getMfaStatus: async (): Promise<{
    enrolled: boolean;
    factor_id?: string;
    friendly_name?: string;
  }> => {
    return apiFetch(versioned("/api/v1/admin/security/mfa"));
  },
  getSecurityLog: async (limit: number = 50): Promise<{ log: unknown[] }> => {
    return apiFetch(versioned(`/api/v1/admin/security/log?limit=${limit}`));
  },

  // -- User Quota -------------------------------------------------
  getUserQuota: async (): Promise<{
    free_remaining: number;
    extra_credits: number;
  }> => {
    return apiFetch(versioned("/api/v1/user/quota"));
  },

  // -- Tutors -----------------------------------------------------
  getTutors: async (): Promise<{ tutors: Tutor[] }> => {
    return apiFetch(versioned("/api/v1/tutors"));
  },
  getMySessions: async (): Promise<{ sessions: unknown[] }> => {
    return apiFetch(versioned("/api/v1/user/sessions"));
  },

  // -- Saved Posts ------------------------------------------------
  getSavedPosts: async (): Promise<{ posts: unknown[] }> => {
    return apiFetch(versioned("/api/v1/user/saved"));
  },

  // -- User Analytics ---------------------------------------------
  getUserAnalytics: async (): Promise<unknown> => {
    return apiFetch(versioned("/api/v1/user/analytics"));
  },

  // -- Daily Bonus ------------------------------------------------
  getDailyBonusStatus: async (): Promise<{
    quiz?: unknown[];
    claimed_today?: boolean;
    score?: number;
    pass_threshold?: number;
    bonus_credits?: number;
  }> => {
    return apiFetch(versioned("/api/v1/daily-bonus/status"));
  },
  submitDailyBonus: async (
    payload: unknown,
  ): Promise<{
    correct: number;
    total: number;
    passed: boolean;
    bonus_awarded: number;
  }> => {
    return apiFetch(versioned("/api/v1/daily-bonus/submit"), {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  // -- Backup Codes -----------------------------------------------
  generateBackupCodes: async (): Promise<{ codes: string[] }> => {
    return apiFetch(versioned("/api/v1/settings/backup-codes/generate"), {
      method: "POST",
    });
  },

  // -- Course Announcements ---------------------------------------
  getCourseAnnouncements: async (
    courseCode: string,
  ): Promise<{ announcements: unknown[] }> => {
    return apiFetch(versioned(`/api/v1/course/${courseCode}/announcements`));
  },
};
export interface KnowledgeHealth {
  course: string;
  chunks: number;
  quality: number;
}

export interface Backup {
  id: string;
  date: string;
  size: string;
  status: "success" | "failed";
}

export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  lastEdited: string;
  content: string;
}

export interface AuditLogEntry {
  id: string;
  timestamp: string;
  user: string;
  action: string;
  details: string;
}

export interface UptimeMonitor {
  name: string;
  status: "up" | "down" | "paused" | "unknown";
  uptime_7d: string;
  response_time: number;
}

export interface ShopItem {
  id: string;
  name: string;
  description: string;
  cost: number;
  category: string;
}

export interface Quest {
  id: string;
  title: string;
  description: string;
  progress: number;
  target: number;
  reward: number;
  status: "active" | "completed" | "claimed";
}

export interface AskResponse {
  answer: unknown;
  sources: Array<Record<string, unknown>>;
  confidence: number;
  low_confidence: boolean;
  /** LLM self-critique quality score (0.1-1.0). 0 means unrated. Read-only display. */
  answer_rating: number;
  /** Multi-dimensional quality rating object from AnswerRater. */
  rating: Record<string, unknown> | null;
  cached: boolean;
  free_remaining: number;
  extra_credits: number;
  difficulty: string;
  is_exam_relevant: boolean;
  code_blocks: Array<Record<string, unknown>>;
  formulas: string[];
  diagrams: Array<Record<string, unknown>>;
  related_concepts: string[];
  socratic_questions: string[];
  prerequisites: string[];
  tts_summary: string;
  audio_url?: string;
  podcast?: boolean;
  podcast_mode?: boolean;
  podcast_duration?: number;
  /** Plain-text podcast transcript ("SPEAKER: text" lines) when supplied. */
  podcast_transcript?: string;
  /** Distinct speaker names featured in a podcast episode, when supplied. */
  podcast_speakers?: string[];
  /** Structured turn dicts (speaker/text/emotion/pace) for advanced clients. */
  podcast_turns?: Array<Record<string, unknown>>;
  inline_image_url?: string;
  engine_output?: Record<string, unknown>;
  /** Which engine produced the answer (e.g. "math", "economics", "general"). */
  engine_used?: string | null;
  /** Honest 300-word-minimum quality gate reported by the backend. */
  quality_gate?: {
    min_words_met: boolean;
    word_count: number;
    cost_within_budget: boolean;
    estimated_cost_usd?: number;
  } | null;
  /** Per-sentence citations linking claims to source chunks. */
  citations?: Array<{
    sentence: string;
    citation_id: string | null;
    chunk_id: string | null;
    title: string | null;
    score: number;
    grounded: boolean;
  }> | null;
  /** Fraction of sentences with a matching source (0..1). */
  citation_coverage?: number | null;
  /** LLM self-verification judge verdict (null when fast / no sources). */
  judge?: {
    grounded: boolean | null;
    correct: boolean | null;
    score: number | null;
    issues: string[];
  } | null;
  images: string[];
  youtube: Array<{
    url: string;
    title?: string;
    channel?: string;
    video_id?: string;
    transcript?: string;
    duration?: number;
    view_count?: number;
    published_at?: string;
  }>;
  steps?: Array<{ step?: number; text?: string }>;
  visualisation?: {
    type:
      | "graph_2d"
      | "graph_3d"
      | "molecule_3d"
      | "crystal_3d"
      | "model_3d"
      | "cad_3d";
    library: "plotly" | "3dmol" | "threejs";
    data: Record<string, unknown>;
  } | null;
  warning?: string;
  summary: string;
  verification?: Record<string, unknown>;
  trust?: {
    verified: boolean;
    grounded_in_course_material: boolean;
    verification_confidence: number;
    mode: string;
  } | null;
  preview_card?: string;
  suggestions?: string[];
  grounding_mode?: string;
  coverage_pct?: number;
  evidence_count?: number;
  evidence_summary?: string;
  // Answer-mode fields
  mode?:
    | "normal"
    | "exam"
    | "manual"
    | "stepwise"
    | "socratic"
    | "podcast"
    | "minimal"
    | "text_image"
    | "genius";
  brief_answer?: string;
  detailed_answer?: string;
  media?: {
    web?: Array<{
      title?: string;
      url?: string;
      snippet?: string;
      source?: string;
    }>;
    images?: Array<{ title?: string; url?: string; image_url?: string }>;
    videos?: Array<{
      title?: string;
      url?: string;
      channel?: string;
      video_id?: string;
    }>;
  };
  download_url?: string;
}

export interface ConversationListResponse {
  conversations: Conversation[];
  total: number;
}

export interface Conversation {
  id: string;
  user_id: string;
  title: string;
  course_code?: string;
  level?: number;
  department?: string;
  archived: boolean;
  pinned: boolean;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface Message {
  id: string;
  conversation_id: string;
  user_id: string;
  role: "user" | "assistant";
  content: string;
  sources?: Array<Record<string, unknown>>;
  engine_output?: Record<string, unknown>;
  verification?: Record<string, unknown>;
  rating?: Record<string, unknown> | null;
  trust?: {
    verified: boolean;
    grounded_in_course_material: boolean;
    verification_confidence: number;
    mode: string;
  } | null;
  image_urls?: string[];
  youtube?: Array<{
    url: string;
    title?: string;
    channel?: string;
    video_id?: string;
    transcript?: string;
    duration?: number;
    view_count?: number;
    published_at?: string;
  }>;
  token_count?: number;
  cost_usd?: number;
  created_at: string;
  updated_at: string;
}

export interface PublicUser {
  id?: string;
  username: string;
  name?: string;
  full_name?: string;
  bio?: string;
  avatar?: string;
  avatar_url?: string | null;
  video_url?: string | null;
  status_videos?: {
    id: string;
    video_url: string;
    caption?: string;
    created_at?: string;
  }[];
  role?: string;
  level?: number | string | null;
  institution?: string;
  school_code?: string;
  department_code?: string;
  is_verified?: boolean;
  badges?: AchievementBadge[];
  skills?: Skill[];
  reputation?: number;
  contributions?: {
    threads: number;
    replies: number;
    credits: number;
    total: number;
  };
  stats?: {
    questions: number;
    answers: number;
    followers: number;
    following: number;
  };
  isFollowing?: boolean;
}

export interface UserNotification {
  id: string;
  message: string;
  timestamp: string;
  icon: string;
  isRead: boolean;
  type?: "info" | "alert" | "success";
  link?: string;
}

export interface AuthSession {
  id: string;
  userAgent: string;
  ip: string;
  createdAt: string;
  expiresAt: string;
  revoked: boolean;
  device?: string;
  browser?: string;
  location?: string;
  lastActive?: string;
  isCurrent?: boolean;
}

export interface SavedPost {
  id: string;
  title: string;
  type: "question" | "note" | "glossary" | "flashcard";
  date: string;
  savedAt?: string;
  preview?: string;
  course?: string;
}

export interface EnrichedNote {
  id: string;
  chunk_id?: string;
  course: string;
  course_code?: string;
  course_title?: string;
  title: string;
  content: string;
  chunks?: DocumentChunk[];
  section_hierarchy?: string;
  week?: number;
  week_number?: number;
  topic?: string;
  parent_topic?: string;
  chunk_type?: string;
  pedagogy_type?: string;
  difficulty?: string | number;
  full_text?: string;
  summary?: string;
  definition?: string;
  rewritten_text?: string;
  formula_latex?: string | string[];
  key_equations?: string[];
  tts_summary?: string;
  code_snippet?: string;
  code_language?: string;
  table_json?: string | unknown;
  key_concepts?: string[];
  misconceptions?: string[];
  step_by_step?: string | string[];
  related_concepts?: string[];
  socratic_questions?: string[];
  hypothetical_questions?: string[];
  prerequisites?: string[];
  unlocks?: string[];
  image_url?: string;
  image_description?: string;
  image_caption?: string;
  page_number?: number;
  is_definition?: boolean;
  requires_calc?: boolean;
  has_figure?: boolean;
  is_exam_relevant?: boolean;
  qa_role?: string;
  qa_question?: string;
  qa_answer?: string;
  qa_options?: unknown;
  kg_edges?: unknown;
}

export interface DocumentChunk {
  id: string;
  content: string;
  source: string;
  chunk_type?: string;
  section_hierarchy?: string;
  formula_latex?: string;
  full_text?: string;
  code_snippet?: string;
  code_language?: string;
  table_json?: string;
  table_json_parsed?: unknown;
  image_url?: string;
  image_caption?: string;
  page_number?: number;
  layers_json?: unknown;
  tts_summary?: string;
  definition?: string;
  step_by_step?: string;
  key_concepts?: string[];
  misconceptions?: string[];
  prerequisites?: string[];
  related_concepts?: string[];
  hypothetical_questions?: string[];
  topic?: string;
  difficulty?: string;
  is_exam_relevant?: boolean;
  week?: number;
}

export interface DocumentView {
  id: string;
  title: string;
  content: string;
  chunks: DocumentChunk[];
  file_url?: string;
  file_type?: string;
  file_size?: number;
  upload_date?: string;
  chunk_count?: number;
  lecturer?: string;
  week?: number;
  course?: string;
  original_filename?: string;
  related_documents?: Array<{ id: string; title: string }>;
}

// =========================================================================
// New feature types
// =========================================================================

export interface AdminDashboardStats {
  totalUsers?: number;
  activeUsers?: number;
  questionsAskedToday?: number;
  revenue?: number;
  dau?: number;
  wau?: number;
  mau?: number;
  [key: string]: unknown;
}

export interface AdminRevenueData {
  date: string;
  revenue_ngn: number;
  transactions: number;
}

export interface AdminHealthReport {
  services: Array<{
    name: string;
    status: "online" | "degraded" | "offline";
    latency?: number;
  }>;
  [key: string]: unknown;
}

export interface AdminUserRow {
  user_id: string;
  full_name?: string;
  email?: string;
  institution?: string;
  department?: string;
  school_code?: string;
  level?: string | number;
  role?: string;
  status?: string;
  extra_credits?: number;
  banned_at?: string | null;
  suspended_until?: string | null;
  created_at?: string;
  [key: string]: unknown;
}

export interface AdminTransaction {
  id: string;
  user_id: string;
  amount_naira: number;
  status: string;
  promo_code?: string;
  monnify_reference: string;
  transaction_ref: string;
  created_at: string;
  verified_at?: string;
  [key: string]: unknown;
}

export interface Ticket {
  id: string;
  subject: string;
  description?: string;
  priority: string;
  status: string;
  user_id: string;
  assigned_to?: string | null;
  created_at: string;
  updated_at: string;
  [key: string]: unknown;
}

export interface TicketDetail extends Ticket {
  messages: TicketMessage[];
}

export interface TicketMessage {
  id: string;
  ticket_id: string;
  sender_id: string;
  author: string;
  body: string;
  created_at: string;
  is_admin: boolean;
}

export default api;
